
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { ShippingRateAnalyzer } from '@/services/ShippingRateAnalyzer';
import {
  Rate,
  AnalyzedRate,
  TagDisplay,
  UserShippingPreferences,
  OrderContext,
  PrimaryTag,
  SecondaryTag
} from '@/types/shipping';
import { GET_SHIPPING_RATES, GET_SHIPPING_RATES_MULTI_PACKAGE, CREATE_SHIPPING_LABEL, GET_ORDER_WITH_SHIPPING_METHOD, GET_TARGETED_SHIPPING_RATE, GET_TARGETED_SHIPPING_RATE_MULTI_PACKAGE, GET_USER_EMAIL } from '@/gql/shipping';
// import { TRACK_SHIPPING_SELECTION } from '@/gql/shipping'; // TODO: Uncomment when backend is ready
import { GET_SHIPPER_ORDER } from '@/gql/shipper';
import queries from '@/gql/queries';
import debounce from 'lodash/debounce';
import { HardwareIntegrationService, getHardwareService } from '@/services/HardwareIntegrationService';
import { printStoredLabel } from '@/services/LabelPdfService';
import { getScannerEventService, ScannerEventService } from '@/services/ScannerEventService';
import { Scale, Printer, Weight, Scanner } from '@/types/hardware';
import { PackageTypeService } from '@/services/PackageTypeService';
import { PackageType, PackageScanEvent } from '@/types/packageTypes';
import { hasRole, WellKnownRoles } from '@/models/User';


@Component
export default class ShippingLabelForm extends Vue {
  @Prop({ required: true }) orderId!: number;
  @Prop({ required: false }) pickTicketId?: number;
  @Prop({ default: true }) testMode!: boolean;
  @Prop({ default: false }) isShipperMode!: boolean;
  /**
   * Mirrors ShipStation:SupportsMultipleNotificationEmails. ShipStation drops the notification
   * entirely when more than one address is supplied (#1154), so while this is false the form
   * pre-populates only the customer email and hides the order-creator opt-in (#1464).
   */
  @Prop({ default: false }) supportsMultipleNotificationEmails!: boolean;
  
  @Watch('selectedPrinterId')
  onSelectedPrinterIdChanged(value: string | null) {
    if (value) {
      localStorage.setItem('shipping_label_printer_id', value);
    } else {
      localStorage.removeItem('shipping_label_printer_id');
    }
  }
  
  @Watch('autoPrintEnabled')
  onAutoPrintEnabledChanged(value: boolean) {
    localStorage.setItem('shipping_label_auto_print', value.toString());
  }

  @Watch('isReturnLabelSupported')
  onReturnLabelSupportChanged(isSupported: boolean) {
    // If return labels are not supported for the selected service, uncheck the option
    if (!isSupported && this.createReturnLabel) {
      this.createReturnLabel = false;
      console.log('[ShippingLabelForm] Return label disabled - service does not support return labels');
    }
  }

  @Watch('packages.length')
  onPackagesLengthChanged() {
    if (this.createReturnLabel && this.isMultiPackageMode) {
      this.initPerPackageRma();
    }
    if (this.perPackageLabelComments && this.isMultiPackageMode) {
      this.initPerPackageLabelComments();
    }
  }

  @Watch('createReturnLabel')
  onCreateReturnLabelChanged(val: boolean) {
    if (val && !this.returnLabelRma) {
      this.defaultReturnLabelRma = this.buildDefaultReturnComment();
      this.returnLabelRma = this.defaultReturnLabelRma;
    }
    // Select all packages by default when enabling return labels
    if (val) {
      this.returnLabelPackageSelections = this.packages.map((_, i) => i + 1);
      // Initialize per-package RMA defaults
      this.initPerPackageRma();
    }
  }

  @Watch('packageDetails.weight')
  @Watch('packageDetails.length')
  @Watch('packageDetails.width')
  @Watch('packageDetails.height')
  onPackageDetailsChanged(newVal: any, oldVal: any) {
    // Skip if this is the initial value setting
    if (!oldVal || !this.autoFetchAttempted) return;

    // Clear auto-selection when package details change significantly
    if (this.hasAutoSelectedRate && oldVal) {
      const weightChange = Math.abs((newVal || 0) - (oldVal || 0));
      // If weight changed by more than 1 oz or dimensions changed, clear auto-selection
      if (weightChange > 1 || (typeof newVal === 'number' && typeof oldVal === 'number' && newVal !== oldVal)) {
        this.hasAutoSelectedRate = false;
        console.log('[ShippingLabelForm] Package details changed significantly, clearing auto-selection');
      }
    }

    // Only auto-fetch if feature is enabled, form is valid and not already loading
    if (this.isAutoFetchEnabled && this.packageFormValid && !this.loadingRates && !this.isAutoFetching) {
      console.log('[ShippingLabelForm] Package details changed, triggering auto-fetch');
      this.debouncedAutoFetchRates?.();
    }
  }

  currentStep = 1;
  packageFormValid = false;
  loadingRates = false;
  generatingLabel = false;

  shipmentId: string | null = null;
  rates: Rate[] = [];
  analyzedRates: AnalyzedRate[] = [];
  rateAnalyzer: ShippingRateAnalyzer | null = null;
  selectedRateId: string | null = null;
  validationErrors: string[] = [];
  showAllRates = false; // Toggle between featured and all rates view
  createdLabelData: any = null;

  
  // User preferences (would normally come from API/store)
  userPreferences: UserShippingPreferences | null = null;
  
  // Hardware integration
  hardwareService: HardwareIntegrationService | null = null;
  hardwareAvailable = false;
  isElectron = 'electron' in window && (window as any).electron?.isElectron === true;
  isUsingUHB = process.env.VUE_APP_USE_UHB === 'true';
  connectedScale: Scale | null = null;
  connectedPrinter: Printer | null = null;
  connectedScanner: Scanner | null = null;
  currentWeight: Weight | null = null;
  isReadingWeight = false;
  hardwareError: string | null = null;
  showHardwareSettings = false;
  availableScales: Scale[] = [];
  availablePrinters: Printer[] = [];
  availableScanners: Scanner[] = [];
  selectedPrinterId: string | null = null;
  autoPrintEnabled = true;

  // Global scanner event service for package QR detection
  scannerEventService: ScannerEventService | null = null;
  isModalVisible = true; // Track if modal is currently visible

  // Package type integration
  packageTypeService = new PackageTypeService();
  savedPackageTypes: PackageType[] = [];
  appliedPackageType: PackageType | null = null;
  isScanning = false;
  
  // Auto-fetch and auto-select properties
  orderShippingMethod: any | null = null;
  defaultCarrierCode: string | null = null;
  defaultServiceCode: string | null = null;
  defaultPackageType: string | null = null;
  
  // Order context information
  orderData: any | null = null;

  // Ship To address override (label-only, does not modify the order)
  editingShipTo = false;
  shipToOverride: { addressLine1: string; addressLine2: string; city: string; state: string; postalCode: string } | null = null;
  shipToEditForm = { addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '' };

  // Email data for shipping notifications
  salesRepEmail: string | null = null;
  orderCreatorEmail: string | null = null;
  customerContactEmail: string | null = null;
  includeOrderCreatorEmail = false;
  notificationEmails: string[] = [];

  // Label message properties for custom text printed on labels
  labelComment1 = '';
  labelComment2 = '';
  labelComment3 = '';
  defaultLabelComment1 = '';
  defaultLabelComment2 = '';
  currentUserInitials = '';
  salesRepInitials = '';
  firstLineItemStyleCode = '';

  // Snapshot tracking: Store the label messages and emails used when shipment was last created
  // This allows us to detect changes and auto-invalidate the cached shipment
  lastFetchedLabelMessages: { reference1: string | null, reference2: string | null, reference3: string | null } | null = null;
  lastFetchedEmails: string | null = null;
  usedTargetedRateFetch = false; // Track if we used targeted rate fetch (so we can use it again on refetch)

  // Track when user manually overrides the auto-selected shipping method
  // so we can preserve their choice when re-fetching rates due to context changes (e.g., email updates)
  userOverrideRate: { carrierCode: string; serviceCode: string } | null = null;

  isAutoFetching = false;
  hasAutoSelectedRate = false;
  autoFetchAttempted = false;
  showManualRateButton = false;
  debouncedAutoFetchRates: any = null; // Will be initialized in created()
  
  // Dual fetching properties
  loadingTargetedRate = false;
  targetedRate: any | null = null;
  hasTargetedRate = false;
  carrierErrors: { carrierCode: string; message: string }[] = [];
  loadingBackgroundRates = false;
  backgroundRates: any[] = []; // Store background rates separately to avoid UI disruption
  
  // Enhanced weight features
  autoReadWeight = true;
  weightPollingInterval: number | null = null;
  previousWeight: number | null = null;
  weightTrend: 'up' | 'down' | 'stable' | null = null;
  isWeightLocked = false;
  lockedWeight: Weight | null = null;
  tareWeight = 0;
  lastWeightUpdate: Date | null = null;
  weightHistory: Weight[] = [];
  maxWeightHistory = 5;
  
  // Multi-package support: packages array (replaces single packageDetails when in multi-package mode)
  packages = [
    {
      id: `pkg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      weight: 16,
      length: 10,
      width: 8,
      height: 4
      // insuredValue: null // Commented out - not required at this time
    }
  ];

  // Multi-package mode flag
  isMultiPackageMode = false;

  // Active package index for targeting scale/QR inputs
  activePackageIndex = 0;

  // Auto-advance to next package after QR scan
  autoAdvancePackage = true;

  // Maximum packages allowed per shipment. Set to 40 to match UPS's standard
  // multi-package label cap (UPS allows up to 40 packages per multi-piece label;
  // ShipStation V2 imposes no count limit). Ideally this would be dynamically
  // determined by the selected carrier's supports_multi_package capability.
  maxPackagesAllowed = 40;

  // Legacy packageDetails - computed property for backward compatibility
  get packageDetails() {
    if (this.isMultiPackageMode) {
      // In multi-package mode, return the active package or first package
      return this.packages[this.activePackageIndex] || this.packages[0];
    } else {
      // In single package mode, return the first (and only) package
      return this.packages[0];
    }
  }

  // Setter for packageDetails to maintain backward compatibility
  set packageDetails(value: any) {
    if (this.isMultiPackageMode) {
      // Update the active package
      if (this.packages[this.activePackageIndex]) {
        Object.assign(this.packages[this.activePackageIndex], value);
      }
    } else {
      // Update the first package
      if (this.packages[0]) {
        Object.assign(this.packages[0], value);
      }
    }
  }

  // Track the last package details used for fetching rates
  lastFetchedPackageDetails: any = null;
  lastFetchedPackages: any = null;
  
  labelFormat = 'pdf';
  labelLayout = '4x6';
  createReturnLabel = false;

  // Return label RMA
  returnLabelRma = '';
  defaultReturnLabelRma = '';
  returnLabelPackageSelections: number[] = [];
  returnLabelRmaPerPackage: { [key: number]: string } = {};
  perPackageRma = false;
  perPackageLabelComments = false;
  labelCommentsPerPackage: { [key: number]: { reference1: string; reference2: string; reference3: string } } = {};
  
  labelFormats = [
    { text: 'PDF', value: 'pdf' },
    { text: 'PNG', value: 'png' },
    { text: 'ZPL', value: 'zpl' }
  ];
  
  labelLayouts = [
    { text: '4x6 - Standard', value: '4x6' },
    { text: '4x8 - Extended', value: '4x8' },
    { text: '8.5x11 (Letter)', value: 'letter' }
  ];
  
  rules = {
    required: (v: any) => !!v || 'Required',
    positive: (v: number) => v > 0 || 'Must be greater than 0'
  };

  // Get validation rules for a package field - allows 0 for empty packages
  getPackageFieldRules(pkg: any) {
    // A package is empty if it has no dimensions (0x0x0)
    // Weight can be auto-filled by scale, so we ignore it for "empty" check
    const isEmpty = pkg.length === 0 && pkg.width === 0 && pkg.height === 0;
    if (isEmpty) {
      return []; // No validation rules for empty packages (dimensions not set yet)
    }
    // Otherwise, require positive values
    return [this.rules.required, this.rules.positive];
  }
  
  // Carrier logo mapping
  carrierLogos: { [key: string]: string } = {
    'usps': require('@/assets/svg-icons/usps-logo.svg'),
    'ups': require('@/assets/svg-icons/ups-logo.svg'),
    'fedex': require('@/assets/svg-icons/fedex-logo.svg'),
    'fedex_walleted': require('@/assets/svg-icons/fedex-logo.svg')
  };
  
  get sortedRates(): Rate[] {
    return [...this.rates]
      .filter(rate => rate && rate.shippingAmount && typeof rate.shippingAmount.amount === 'number')
      .sort((a, b) => a.shippingAmount.amount - b.shippingAmount.amount);
  }

  get emailValidationRules(): Array<(v: any) => boolean | string> {
    return [
      (v: any) => {
        if (!v || (Array.isArray(v) && v.length === 0)) return true; // Optional field

        const emails = Array.isArray(v) ? v : v.split(',').map((e: string) => e.trim());
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        for (const email of emails) {
          const trimmed = typeof email === 'string' ? email.trim() : email;
          if (trimmed && !emailRegex.test(trimmed)) {
            return `"${trimmed}" is not a valid email address`;
          }
        }
        return true;
      }
    ];
  }
  
  get selectedRate(): Rate | undefined {
    return this.rates.find(r => r.rateId === this.selectedRateId);
  }

  get isReturnLabelSupported(): boolean {
    if (!this.selectedRate) return true; // Allow by default if no rate selected

    // UPS Ground Saver does not support return labels
    // Source: https://help.shipstation.com/hc/en-us/articles/360035966572-UPS-Ground-Saver-formerly-UPS-SurePost#UUID-275185f7-414b-ebc6-5c80-951e463f26a4_UUID-94006792-12a9-fd3c-fc23-85ceaa22509c
    const serviceCode = this.selectedRate.serviceCode?.toLowerCase();
    return serviceCode !== 'ups_ground_saver';
  }

  get returnLabelTooltip(): string {
    if (this.isReturnLabelSupported) {
      return 'Create a return shipping label with reversed ship-to and ship-from addresses';
    }
    return 'Return labels are not supported for UPS Ground Saver service.';
  }

  get cheapestRate(): Rate | undefined {
    if (this.sortedRates.length === 0) return undefined;
    return this.sortedRates[0]; // Already sorted by price
  }
  
  get fastestRate(): Rate | undefined {
    if (this.rates.length === 0) return undefined;
    
    const validRates = this.rates
      .filter(rate => rate && rate.deliveryDays != null && rate.deliveryDays > 0)
      .sort((a, b) => (a.deliveryDays || 0) - (b.deliveryDays || 0));
    
    return validRates[0];
  }
  
  get recommendedRate(): Rate | undefined {
    if (this.rates.length === 0) return undefined;
    
    // Initialize analyzer if needed
    if (!this.rateAnalyzer) {
      const orderContext: OrderContext = {
        orderId: this.orderId,
        destinationZone: this.rates[0]?.zone
      };
      
      this.rateAnalyzer = new ShippingRateAnalyzer(
        this.rates,
        this.userPreferences || undefined,
        orderContext
      );
      this.analyzedRates = this.rateAnalyzer.analyzeRates();
    }
    
    // Find the rate with the highest overall score
    const bestRate = this.analyzedRates.reduce((best, current) => {
      return current.analysis.scores.overall > best.analysis.scores.overall ? current : best;
    }, this.analyzedRates[0]);
    
    return this.rates.find(r => r.rateId === bestRate?.rateId);
  }
  
  get bestValueRate(): Rate | undefined {
    if (this.rates.length === 0) return undefined;
    
    // Initialize analyzer if needed
    if (!this.rateAnalyzer) {
      const orderContext: OrderContext = {
        orderId: this.orderId,
        destinationZone: this.rates[0]?.zone
      };
      
      this.rateAnalyzer = new ShippingRateAnalyzer(
        this.rates,
        this.userPreferences || undefined,
        orderContext
      );
      this.analyzedRates = this.rateAnalyzer.analyzeRates();
    }
    
    // Find the rate with the best value score (balance of price and speed)
    // Exclude the cheapest and fastest to ensure we get a different option
    const cheapestId = this.cheapestRate?.rateId;
    const fastestId = this.fastestRate?.rateId;
    
    const valueRates = this.analyzedRates
      .filter(r => r.rateId !== cheapestId && r.rateId !== fastestId)
      .sort((a, b) => b.analysis.scores.value - a.analysis.scores.value);
    
    if (valueRates.length > 0) {
      return this.rates.find(r => r.rateId === valueRates[0].rateId);
    }
    
    // Fallback to recommended rate if no distinct value rate
    return this.recommendedRate;
  }
  
  get featuredRates(): { rate: Rate; type: 'cheapest' | 'recommended' | 'fastest'; label: string; icon: string; color: string }[] {
    const featured: Array<{ rate: Rate; type: 'cheapest' | 'recommended' | 'fastest'; label: string; icon: string; color: string }> = [];
    
    // Always show cheapest if available
    if (this.cheapestRate) {
      featured.push({
        rate: this.cheapestRate,
        type: 'cheapest',
        label: 'Cheapest',
        icon: 'mdi-currency-usd',
        color: 'success'
      });
    }
    
    // Always show fastest if available and different from cheapest
    if (this.fastestRate && this.fastestRate.rateId !== this.cheapestRate?.rateId) {
      featured.push({
        rate: this.fastestRate,
        type: 'fastest',
        label: 'Fastest',
        icon: 'mdi-lightning-bolt',
        color: 'orange'
      });
    }
    
    // Show best value (or recommended as fallback) as third option
    const bestValue = this.bestValueRate;
    if (bestValue && 
        bestValue.rateId !== this.cheapestRate?.rateId && 
        bestValue.rateId !== this.fastestRate?.rateId) {
      featured.push({
        rate: bestValue,
        type: 'recommended',
        label: 'Best Value',
        icon: 'mdi-star',
        color: 'primary'
      });
    } else if (this.recommendedRate && 
               this.recommendedRate.rateId !== this.cheapestRate?.rateId && 
               this.recommendedRate.rateId !== this.fastestRate?.rateId) {
      // Fallback to recommended if no distinct best value
      featured.push({
        rate: this.recommendedRate,
        type: 'recommended',
        label: 'Recommended',
        icon: 'mdi-thumb-up',
        color: 'primary'
      });
    }
    
    return featured;
  }

  // Multi-package computed properties
  get totalWeight(): number {
    return this.packages.reduce((total, pkg) => total + (pkg.weight || 0), 0);
  }

  get packageCount(): number {
    return this.packages.length;
  }

  get canAddMorePackages(): boolean {
    return this.packages.length < this.maxPackagesAllowed;
  }

  get selectedCarrierSupportsMultiPackage(): boolean {
    // TODO: Check actual carrier support based on selected shipping method
    // For now, return true to enable the feature
    return true;
  }

  get canUseMultiPackage(): boolean {
    // Check if multi-package is supported and enabled
    return this.selectedCarrierSupportsMultiPackage && process.env.VUE_APP_ENABLE_MULTI_PACKAGE !== 'false';
  }

  // Package parameter tracking for automatic rate refetching
  get packageParametersHash(): string {
    if (this.isMultiPackageMode) {
      return this.packages.map(pkg =>
        `${pkg.weight || 0}-${pkg.length || 0}-${pkg.width || 0}-${pkg.height || 0}`
      ).join('|');
    } else {
      const pkg = this.packages[0];
      return `${pkg?.weight || 0}-${pkg?.length || 0}-${pkg?.width || 0}-${pkg?.height || 0}`;
    }
  }

  get multiPackageWarningMessage(): string | null {
    if (!this.isMultiPackageMode) return null;

    if (!this.selectedCarrierSupportsMultiPackage) {
      return 'Selected carrier does not support multi-package shipments';
    }

    if (this.packages.length > this.maxPackagesAllowed) {
      return `Cannot exceed ${this.maxPackagesAllowed} packages per shipment`;
    }

    return null;
  }

  getCarrierLogo(carrierCode: string): string | null {
    const code = carrierCode?.toLowerCase();
    return this.carrierLogos[code] || null;
  }
  
  getCarrierIcon(carrierCode: string): string {
    const code = carrierCode?.toLowerCase();
    switch (code) {
      case 'ups': return 'mdi-truck-delivery';
      case 'fedex': return 'mdi-truck-fast';
      case 'usps': return 'mdi-email-fast';
      case 'dhl': return 'mdi-airplane';
      default: return 'mdi-truck';
    }
  }
  
  formatDeliveryDate(date: string | Date | null): string {
    if (!date) return 'N/A';
    
    const deliveryDate = new Date(date);
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    };
    
    return deliveryDate.toLocaleDateString('en-US', options);
  }

  getSelectedPrinterName(): string {
    if (!this.selectedPrinterId) return '';
    const printer = this.availablePrinters.find(p => p.id === this.selectedPrinterId);
    return printer?.name || 'Unknown Printer';
  }

  openPrinterSettings() {
    // Emit event to open the hardware status panel and show printer settings
    this.$bus?.$emit('hardware-status-show', { openPrinterSettings: true });
    (this as any).$toast?.info('Opening printer settings...');
  }

  onPrinterSettingsUpdated(settings: any) {
    // Update local settings when printer configuration changes
    this.selectedPrinterId = settings.selectedPrinterId;
    this.autoPrintEnabled = settings.autoPrintEnabled;

    // Save to localStorage as well (redundant but ensures consistency)
    localStorage.setItem('shipping_label_auto_print', settings.autoPrintEnabled ? 'true' : 'false');
    if (settings.selectedPrinterId) {
      localStorage.setItem('shipping_label_printer_id', settings.selectedPrinterId);
    }
  }
  
  // Enhanced rate tagging using the ShippingRateAnalyzer
  getRateTags(rate: Rate): TagDisplay[] {
    // Initialize analyzer if needed or if rates have changed
    if (!this.rateAnalyzer || this.analyzedRates.length === 0) {
      const orderContext: OrderContext = {
        orderId: this.orderId,
        destinationZone: rate.zone
      };
      
      this.rateAnalyzer = new ShippingRateAnalyzer(
        this.rates,
        this.userPreferences || undefined,
        orderContext
      );
      this.analyzedRates = this.rateAnalyzer.analyzeRates();
    }
    
    const analyzed = this.analyzedRates.find(r => r.rateId === rate.rateId);
    if (!analyzed) return [];
    
    const tags: TagDisplay[] = [];
    const { primary, secondary, contextual, badges } = analyzed.analysis.tags;
    
    // Add primary tag (highest priority)
    if (primary) {
      tags.push({
        text: primary,
        color: this.getPrimaryTagColor(primary),
        priority: 1,
        icon: this.getPrimaryTagIcon(primary)
      });
    }
    
    // Add secondary tags
    secondary.forEach(tag => {
      tags.push({
        text: tag,
        color: this.getSecondaryTagColor(tag),
        priority: 2
      });
    });
    
    // Add up to 2 contextual tags
    contextual.slice(0, 2).forEach(tag => {
      tags.push({
        text: tag,
        color: 'blue-grey',
        priority: 3,
        small: true
      });
    });
    
    // Add up to 1 badge
    badges.slice(0, 1).forEach(badge => {
      tags.push({
        text: badge,
        color: 'transparent',
        textColor: 'grey darken-1',
        priority: 4,
        outlined: true,
        small: true
      });
    });
    
    // Sort by priority and limit total tags shown
    return tags
      .sort((a, b) => a.priority - b.priority)
      .slice(0, 4); // Show max 4 tags per rate
  }
  
  private getPrimaryTagColor(tag: PrimaryTag): string {
    switch (tag) {
      case PrimaryTag.BEST_OVERALL: return 'primary';
      case PrimaryTag.CHEAPEST: return 'success';
      case PrimaryTag.FASTEST: return 'blue';
      case PrimaryTag.RECOMMENDED: return 'deep-purple';
      default: return 'grey';
    }
  }
  
  private getPrimaryTagIcon(tag: PrimaryTag): string {
    switch (tag) {
      case PrimaryTag.BEST_OVERALL: return 'mdi-trophy';
      case PrimaryTag.CHEAPEST: return 'mdi-currency-usd';
      case PrimaryTag.FASTEST: return 'mdi-lightning-bolt';
      case PrimaryTag.RECOMMENDED: return 'mdi-star';
      default: return '';
    }
  }
  
  private getSecondaryTagColor(tag: SecondaryTag): string {
    switch (tag) {
      case SecondaryTag.GREAT_VALUE: return 'teal';
      case SecondaryTag.EXPRESS: return 'red accent-2';
      case SecondaryTag.ECONOMY: return 'green darken-2';
      case SecondaryTag.PREMIUM: return 'indigo';
      case SecondaryTag.STANDARD: return 'blue-grey';
      default: return 'grey';
    }
  }
  
  // New computed properties for auto-fetch flow
  get canProceedFromStep1(): boolean {
    // Can proceed if:
    // 1. Form is valid AND
    // 2. We have rates available (either targeted or full rates) OR
    // 3. We're not in the initial loading phase
    return this.packageFormValid && (
      // If we have a targeted rate or selected rate, we can proceed
      (this.selectedRateId && (this.hasAutoSelectedRate || this.hasTargetedRate)) || 
      // If we have any rates (targeted or full), we can proceed
      this.hasValidRates ||
      // If we're not loading anything initially (manual mode)
      (!this.loadingRates && !this.isAutoFetching && !this.loadingTargetedRate && !this.loadingBackgroundRates)
    );
  }
  
  get shouldShowReviewScreen(): boolean {
    // Show review screen when we have an auto-selected rate (either from auto-selection or targeted fetch)
    return this.currentStep === 2 && (this.hasAutoSelectedRate || this.hasTargetedRate) && this.selectedRateId !== null;
  }
  
  get isRateSelectionComplete(): boolean {
    return this.selectedRateId !== null;
  }

  get isShipperUser(): boolean {
    // Check if explicitly in shipper mode or if user has shipper role
    if (this.isShipperMode) return true;
    const user = this.$store.getters.userInfo;
    return user && hasRole(user, WellKnownRoles.SHIPPER);
  }

  get shouldSkipRateSelection(): boolean {
    // Shipper users should skip rate selection if we have a targeted rate
    return this.isShipperUser && this.hasTargetedRate;
  }

  /** Returns the address to display — override if set, otherwise the original */
  get displayShipToAddress() {
    if (this.shipToOverride) {
      return {
        street1: this.shipToOverride.addressLine1,
        street2: this.shipToOverride.addressLine2,
        city: this.shipToOverride.city,
        state: this.shipToOverride.state,
        postalCode: this.shipToOverride.postalCode
      };
    }
    return this.orderData?.shippingAddress || {};
  }

  /** Returns the shipToOverride in the format the GraphQL input expects, or undefined */
  get shipToOverrideInput(): any {
    if (!this.shipToOverride) return undefined;
    return {
      addressLine1: this.shipToOverride.addressLine1,
      addressLine2: this.shipToOverride.addressLine2 || null,
      city: this.shipToOverride.city,
      state: this.shipToOverride.state,
      postalCode: this.shipToOverride.postalCode,
      countryCode: 'US'
    };
  }

  startShipToEdit() {
    const addr = this.displayShipToAddress;
    this.shipToEditForm = {
      addressLine1: addr.street1 || '',
      addressLine2: addr.street2 || '',
      city: addr.city || '',
      state: addr.state || '',
      postalCode: addr.postalCode || ''
    };
    this.editingShipTo = true;
  }

  saveShipToOverride() {
    if (!this.shipToEditForm.addressLine1 || !this.shipToEditForm.city || !this.shipToEditForm.state || !this.shipToEditForm.postalCode) {
      return; // Basic validation - require key fields
    }
    this.shipToOverride = { ...this.shipToEditForm };
    this.editingShipTo = false;
    console.log('[ShippingLabelForm] Ship To override set:', this.shipToOverride);
    this.refetchRatesForAddressChange();
  }

  cancelShipToEdit() {
    this.editingShipTo = false;
  }

  resetShipToOverride() {
    this.shipToOverride = null;
    console.log('[ShippingLabelForm] Ship To override cleared, using original address');
    this.refetchRatesForAddressChange();
  }

  /** Re-fetch rates after an address change, preserving the streamlined flow when possible */
  private async refetchRatesForAddressChange() {
    // Clear the existing shipment since the address changed
    this.shipmentId = null;
    this.selectedRateId = null;
    this.userOverrideRate = null;

    // Re-fetch targeted rate if we have carrier/service defaults (preserves streamlined flow)
    if (this.defaultCarrierCode && this.defaultServiceCode) {
      this.hasTargetedRate = false;
      await this.fetchTargetedRate();
    } else {
      // Fall back to full rate fetch
      this.hasTargetedRate = false;
      this.getRates();
    }
  }

  async selectRate(rateId: string) {
    this.selectedRateId = rateId;

    // Track manual override: if user selects a rate different from the auto-selected targeted rate,
    // store the carrier/service so we can preserve their choice on re-fetch (e.g., when emails change)
    const selectedRate = this.rates.find(r => r.rateId === rateId);
    if (selectedRate) {
      const isOverride = this.targetedRate && (
        selectedRate.carrierCode !== this.targetedRate.carrierCode ||
        selectedRate.serviceCode !== this.targetedRate.serviceCode
      );
      if (isOverride) {
        this.userOverrideRate = {
          carrierCode: selectedRate.carrierCode || '',
          serviceCode: selectedRate.serviceCode
        };
        console.log('[ShippingLabelForm] User overrode shipping method:', this.userOverrideRate);
      } else {
        // User re-selected the default, clear override
        this.userOverrideRate = null;
      }
    }

    // TODO: Track the selection for learning when backend mutation is implemented
    // const analyzed = this.analyzedRates.find(r => r.rateId === rateId);
    // if (analyzed && this.$apollo) {
    //   try {
    //     await this.$apollo.mutate({
    //       mutation: TRACK_SHIPPING_SELECTION,
    //       variables: {
    //         input: {
    //           orderId: this.orderId,
    //           selectedRateId: rateId,
    //           carrierCode: analyzed.carrierCode,
    //           serviceCode: analyzed.serviceCode,
    //           shippingCost: analyzed.shippingAmount.amount,
    //           deliveryDays: analyzed.deliveryDays,
    //           scores: {
    //             overall: analyzed.analysis.scores.overall,
    //             price: analyzed.analysis.scores.price,
    //             speed: analyzed.analysis.scores.speed,
    //             reliability: analyzed.analysis.scores.reliability,
    //             value: analyzed.analysis.scores.value
    //           },
    //           tags: {
    //             primary: analyzed.analysis.tags.primary,
    //             secondary: analyzed.analysis.tags.secondary
    //           }
    //         }
    //       }
    //     });
    //   } catch (error) {
    //     // Don't block UI if tracking fails
    //     console.warn('Failed to track shipping selection:', error);
    //   }
    // }
  }

  // Multi-package helper methods
  generatePackageId(): string {
    return `pkg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  addPackage(initializeEmpty: boolean = false): void {
    if (!this.canAddMorePackages) {
      this.$emit('error', `Cannot add more than ${this.maxPackagesAllowed} packages`);
      return;
    }

    const newPackage = initializeEmpty ? {
      id: this.generatePackageId(),
      weight: 0, // Empty/null weight for auto-created packages
      length: 0, // Empty/null dimensions
      width: 0,
      height: 0
      // insuredValue: null // Commented out - not required at this time
    } : {
      id: this.generatePackageId(),
      weight: 16, // Default weight
      length: 10, // Default dimensions
      width: 8,
      height: 4
      // insuredValue: null // Commented out - not required at this time
    };

    this.packages.push(newPackage);
    this.activePackageIndex = this.packages.length - 1; // Select the new package

    console.log(`[ShippingLabelForm] Added and selected new package ${this.packages.length} (${initializeEmpty ? 'empty initialization' : 'default values'})`);

    this.clearRatesAfterPackageChange();
  }

  removePackage(index: number): void {
    if (this.packages.length <= 1) {
      this.$emit('error', 'At least one package is required');
      return;
    }

    if (index < 0 || index >= this.packages.length) {
      console.error(`[ShippingLabelForm] Invalid package index: ${index}`);
      return;
    }

    this.packages.splice(index, 1);

    // Adjust active package index after removal
    if (this.activePackageIndex >= this.packages.length) {
      this.activePackageIndex = this.packages.length - 1;
    } else if (this.activePackageIndex > index) {
      this.activePackageIndex--;
    }

    console.log(`[ShippingLabelForm] Removed package, active package is now ${this.activePackageIndex + 1}`);

    this.clearRatesAfterPackageChange();
  }

  duplicatePackage(index: number): void {
    if (!this.canAddMorePackages) {
      this.$emit('error', `Cannot add more than ${this.maxPackagesAllowed} packages`);
      return;
    }

    if (index < 0 || index >= this.packages.length) {
      console.error(`[ShippingLabelForm] Invalid package index: ${index}`);
      return;
    }

    const packageToDuplicate = this.packages[index];
    const duplicatedPackage = {
      ...packageToDuplicate,
      id: this.generatePackageId() // New unique ID
    };

    this.packages.push(duplicatedPackage);
    this.activePackageIndex = this.packages.length - 1; // Select the duplicated package

    console.log(`[ShippingLabelForm] Duplicated package ${index + 1}, selected new package ${this.packages.length}`);

    this.clearRatesAfterPackageChange();
  }

  clearAllPackages(): void {
    this.packages = [
      {
        id: this.generatePackageId(),
        weight: 16,
        length: 10,
        width: 8,
        height: 4
        // insuredValue: null // Commented out - not required at this time
      }
    ];
    this.activePackageIndex = 0; // Reset to first package
    this.clearRatesAfterPackageChange();
  }

  selectPackage(index: number): void {
    if (index >= 0 && index < this.packages.length) {
      this.activePackageIndex = index;
      console.log(`[ShippingLabelForm] Selected package ${index + 1} as active for scale/QR input`);

      // Optional: Add visual feedback for selection
      this.$nextTick(() => {
        const element = this.$el.querySelector(`.package-card[data-package-index="${index}"]`);
        if (element) {
          element.classList.add('package-card--flash');
          setTimeout(() => element.classList.remove('package-card--flash'), 500);
        }
      });
    }
  }

  onMultiPackageToggle(): void {
    console.log(`[ShippingLabelForm] Multi-package mode toggled: ${this.isMultiPackageMode}`);

    if (!this.isMultiPackageMode) {
      // Switching to single package mode - keep only the first package
      this.packages = [this.packages[0]];
      this.activePackageIndex = 0;
    } else {
      // Switching to multi-package mode - ensure we have at least one package
      if (this.packages.length === 0) {
        this.clearAllPackages();
      }
    }

    this.clearRatesAfterPackageChange();
  }

  clearRatesAfterPackageChange(): void {
    // Clear rates and related state when packages change
    this.rates = [];
    this.selectedRateId = null;
    this.hasAutoSelectedRate = false;
    this.hasTargetedRate = false;
    this.shipmentId = null;
    this.userOverrideRate = null;
    this.lastFetchedPackageDetails = null;
    this.lastFetchedPackages = null;

    console.log('[ShippingLabelForm] Cleared rates after package configuration change');
  }

  // Filter out packages with no dimensions (empty packages created via auto-advance)
  getValidPackages() {
    return this.packages.filter(pkg => {
      // A valid package must have dimensions set (L×W×H all > 0)
      // Weight can be auto-filled by scale but dimensions indicate user intent
      const hasDimensions = pkg.length > 0 && pkg.width > 0 && pkg.height > 0;
      return hasDimensions;
    });
  }

  // Check if a package is empty (will be skipped)
  isPackageEmpty(pkg: any): boolean {
    // Empty packages have no dimensions (0x0x0)
    return pkg.length === 0 && pkg.width === 0 && pkg.height === 0;
  }

  created() {
    // Initialize debounced auto-fetch function
    this.debouncedAutoFetchRates = debounce(() => {
      if (this.packageFormValid && !this.loadingRates) {
        this.autoFetchRates();
      }
    }, 800); // 800ms delay
  }
  
  get isAutoFetchEnabled(): boolean {
    // Feature flag to enable/disable auto-fetch
    return process.env.VUE_APP_ENABLE_AUTO_FETCH !== 'false';
  }

  async mounted() {
    // Clear any stale state from previous usage
    this.backgroundRates = [];
    this.hasTargetedRate = false;
    this.hasAutoSelectedRate = false;

    // Initialize hardware service
    await this.initializeHardware();

    // Load saved package types
    await this.loadPackageTypes();

    // Listen for printer settings updates
    this.$bus?.$on('printer-settings-updated', this.onPrinterSettingsUpdated);

    // Initialize global scanner event service for package QR detection
    console.log('[ShippingLabelForm] Setting up global scanner event service');
    this.scannerEventService = getScannerEventService();
    console.log('[ShippingLabelForm] Scanner event service:', this.scannerEventService);
    console.log('[ShippingLabelForm] Adding package-qr-scanned event listener');
    this.scannerEventService.on('package-qr-scanned', this.handleGlobalPackageScan);

    // Only run auto-fetch logic if feature is enabled
    if (this.isAutoFetchEnabled) {
      // Fetch order shipping method for auto-selection
      // CRITICAL: Must complete before initializeLabelComments runs
      console.log('[ShippingLabelForm] Starting fetchOrderShippingMethod...');
      await this.fetchOrderShippingMethod();
      console.log('[ShippingLabelForm] fetchOrderShippingMethod COMPLETED. orderData:', this.orderData ? 'loaded' : 'null');
      console.log('[ShippingLabelForm] Shipping method after fetch:', {
        defaultCarrierCode: this.defaultCarrierCode,
        defaultServiceCode: this.defaultServiceCode
      });

      // Initialize label comments with default values (after order data is loaded)
      await this.initializeLabelComments();

      // Wait for Vue to process all reactive state changes from data loading
      await this.$nextTick();

      // Always schedule auto-fetch - autoFetchRates() will validate the form itself
      // Removed packageFormValid check here as Vuetify forms don't validate synchronously at mount time
      setTimeout(() => {
        this.autoFetchRates();
      }, 500); // Small delay to ensure UI is ready
    }

    // Disable console logging in production for better performance
    if (process.env.NODE_ENV === 'production') {
      const noop = () => {};
      ['log', 'debug', 'info'].forEach(method => {
        (console as any)[method] = noop;
      });
    }
  }

  // Keyboard shortcuts removed to prevent interference with QR code scanning

  get hasDefaultShippingConfig(): boolean {
    return !!(this.defaultCarrierCode && this.defaultServiceCode);
  }

  get hasValidRates(): boolean {
    // Has valid rates if we have either:
    // 1. Full rates loaded OR
    // 2. A targeted rate that was successfully fetched
    return (this.rates.length > 0 || this.hasTargetedRate) && this.arePackageDetailsUnchanged();
  }
  
  arePackageDetailsUnchanged(): boolean {
    if (this.isMultiPackageMode) {
      // Multi-package mode: check if packages array has changed
      if (!this.lastFetchedPackages) return false;

      const currentValidPackages = this.getValidPackages();

      // Check if package count changed
      if (currentValidPackages.length !== this.lastFetchedPackages.length) {
        return false;
      }

      // Check if any package details changed
      return currentValidPackages.every((currentPkg, index) => {
        const lastPkg = this.lastFetchedPackages[index];
        return (
          currentPkg.weight === lastPkg.weight &&
          currentPkg.length === lastPkg.length &&
          currentPkg.width === lastPkg.width &&
          currentPkg.height === lastPkg.height
        );
      });
    } else {
      // Single package mode: check packageDetails object
      if (!this.lastFetchedPackageDetails) return false;

      return (
        this.packageDetails.weight === this.lastFetchedPackageDetails.weight &&
        this.packageDetails.length === this.lastFetchedPackageDetails.length &&
        this.packageDetails.width === this.lastFetchedPackageDetails.width &&
        this.packageDetails.height === this.lastFetchedPackageDetails.height
      );
    }
  }
  
  proceedToRates() {
    if (this.hasValidRates) {
      // User clicked "Next" - advance to step 2
      this.currentStep = 2;
    } else {
      // No rates yet - fetch them first
      this.getRates();
    }
  }
  
  proceedToNextStep() {
    // Handle proceeding from Step 1 when user clicks the button
    if (this.currentStep === 1) {
      // If package details changed and we have a default shipping config, re-fetch rates
      // instead of proceeding with stale data
      if (!this.arePackageDetailsUnchanged() && this.hasDefaultShippingConfig) {
        this.autoFetchRates();
        return;
      }
      // For shipper users with targeted rate, always skip to review
      if (this.shouldSkipRateSelection) {
        this.currentStep = 3;
        this.showAllRates = false;
      } else if (this.selectedRateId && (this.hasAutoSelectedRate || this.hasTargetedRate)) {
        // Skip to step 3 (review) if we have an auto-selected or targeted rate
        this.currentStep = 3;
        // Reset the rates view when proceeding to review
        this.showAllRates = false;
      } else if (this.rates.length > 0 || this.hasTargetedRate) {
        // Go to step 2 (rate selection) if we have rates but no auto-selection
        this.currentStep = 2;
        // Always start with featured rates view
        this.showAllRates = false;
      } else {
        // Fetch rates if we don't have any
        this.getRates();
      }
    }
  }
  
  proceedToReview() {
    if (this.selectedRateId) {
      this.currentStep = 3;
      // Reset the rates view when proceeding to review
      this.showAllRates = false;
    }
  }


  viewOtherShippingOptions() {
    console.log('[ShippingLabelForm] User requested to view other shipping options');

    // Just do the same as the back button
    this.goBack();
  }

  async getRates() {
    if (!this.packageFormValid) return;
    
    this.loadingRates = true;
    try {
      let ratesResponse;

      if (this.isMultiPackageMode) {
        // Multi-package mode: use multi-package GraphQL query
        // Filter out empty packages (created via auto-advance)
        const validPackages = this.getValidPackages();

        if (validPackages.length === 0) {
          console.warn('[ShippingLabelForm] No valid packages to get rates for');
          return;
        }

        console.log('MULTI_PACKAGE_DEBUG: Getting multi-package shipping rates:', {
          orderId: this.orderId,
          pickTicketId: this.pickTicketId,
          totalPackages: this.packages.length,
          validPackages: validPackages.length,
          totalWeight: `${this.totalWeight}oz`,
          packages: validPackages.map((pkg, index) => ({
            package: index + 1,
            id: pkg.id,
            weight: `${pkg.weight}oz`,
            dimensions: `${pkg.length}x${pkg.width}x${pkg.height}in`,
            externalPackageId: pkg.id
          })),
          timestamp: new Date().toISOString()
        });

        // Check for multi-package warnings before making the request
        if (this.multiPackageWarningMessage) {
          this.$emit('error', this.multiPackageWarningMessage);
          return;
        }

        const multiPackageDetailsInput = this.buildMultiPackageInput(validPackages);

        console.log('MULTI_PACKAGE_DEBUG: GraphQL query variables:', {
          orderId: this.orderId,
          pickTicketId: this.pickTicketId,
          multiPackageDetails: multiPackageDetailsInput
        });

        ratesResponse = await this.$apollo.query({
          query: GET_SHIPPING_RATES_MULTI_PACKAGE,
          variables: {
            orderId: this.orderId,
            pickTicketId: this.pickTicketId || null,
            multiPackageDetails: multiPackageDetailsInput,
            emails: this.getEmailsAsString() || null,
            labelMessages: this.getLabelMessagesInput(),
            shipToOverride: this.shipToOverrideInput
          },
          fetchPolicy: 'network-only'
        });
      } else {
        // Single package mode: use legacy single-package GraphQL query
        console.log('DIAGNOSTIC: Getting single-package shipping rates:', {
          orderId: this.orderId,
          pickTicketId: this.pickTicketId,
          weight: `${this.packageDetails.weight}oz`,
          dimensions: `${this.packageDetails.length}x${this.packageDetails.width}x${this.packageDetails.height}in`,
          timestamp: new Date().toISOString()
        });

        const packageDetailsInput = {
          weight: this.packageDetails.weight,
          length: this.packageDetails.length,
          width: this.packageDetails.width,
          height: this.packageDetails.height
        };

        console.log('DIAGNOSTIC: GraphQL query variables:', {
          orderId: this.orderId,
          pickTicketId: this.pickTicketId,
          packageDetails: packageDetailsInput
        });

        ratesResponse = await this.$apollo.query({
          query: GET_SHIPPING_RATES,
          variables: {
            orderId: this.orderId,
            pickTicketId: this.pickTicketId || null,
            packageDetails: packageDetailsInput,
            emails: this.getEmailsAsString() || null,
            labelMessages: this.getLabelMessagesInput(),
            shipToOverride: this.shipToOverrideInput
          },
          fetchPolicy: 'network-only'
        });
      }
      
      console.log('DIAGNOSTIC: GraphQL response:', {
        data: ratesResponse.data,
        timestamp: new Date().toISOString()
      });
      
      const shippingRatesData = ratesResponse.data.shippingRates;
      this.shipmentId = shippingRatesData.shipmentId;
      console.log('DIAGNOSTIC: Shipment created successfully - ID:', this.shipmentId);

      // SNAPSHOT: Save the label messages and emails used for this shipment creation
      // This allows us to detect changes later and auto-invalidate the cached shipment
      this.lastFetchedLabelMessages = {
        reference1: this.labelComment1 || null,
        reference2: this.labelComment2 || null,
        reference3: this.labelComment3 || null
      };
      this.lastFetchedEmails = this.getEmailsAsString() || null;
      this.usedTargetedRateFetch = false; // Mark that we used regular rate fetch
      console.log('[ShippingLabelForm] Snapshotted shipment context:', {
        shipmentId: this.shipmentId,
        labelMessages: this.lastFetchedLabelMessages,
        emails: this.lastFetchedEmails,
        usedTargetedRateFetch: false
      });

      console.log('DIAGNOSTIC: Rates response summary:', {
        shipmentId: shippingRatesData.shipmentId,
        validRates: shippingRatesData.rates?.length || 0
      });
      
      // Rates from GraphQL are already in camelCase
      this.rates = shippingRatesData.rates || [];
      
      // Reset analyzer when new rates are loaded
      this.rateAnalyzer = null;
      this.analyzedRates = [];
      
      // Clear previous validation errors
      this.validationErrors = [];
      
      // If no rates at all, store carrier-specific errors for inline display
      if (this.rates.length === 0) {
        console.error('DIAGNOSTIC: No rates available!');
        console.error('DIAGNOSTIC: Response data:', ratesResponse.data);

        this.carrierErrors = shippingRatesData.errors || [];
        if (this.carrierErrors.length === 0) {
          // No specific carrier errors — set a generic one for inline display
          this.carrierErrors = [{
            carrierCode: '',
            message: 'No shipping rates available. The shipping address may not be serviceable or package specifications don\'t meet carrier requirements.'
          }];
        }
        return;
      }
      
      // Only clear selection if this is a manual fetch or rates have changed
      if (!this.isAutoFetching && !this.hasAutoSelectedRate) {
        this.selectedRateId = null;
      }
      
      // Update state after successful rate fetch
      if (this.rates.length > 0) {
        // Save the package details used for this fetch
        if (this.isMultiPackageMode) {
          this.lastFetchedPackages = JSON.parse(JSON.stringify(this.getValidPackages()));
        } else {
          this.lastFetchedPackageDetails = { ...this.packageDetails };
        }
        this.showAllRates = false; // Reset to featured view
        console.log('[ShippingLabelForm] Rates loaded successfully - ready for user to proceed');
      } else {
        this.$emit('error', 'No shipping rates available. Please check the address and try again.');
      }
    } catch (error: any) {
      console.error('DIAGNOSTIC: Error getting rates:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
        timestamp: new Date().toISOString()
      });
      
      // Extract error details from GraphQL response
      let errorMessage = 'Error getting shipping rates';
      let errorDetails = null;
      
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        errorMessage = graphQLError.message;
        
        // Check for ShipStation specific errors with additional details
        if (graphQLError.extensions) {
          errorDetails = {
            code: graphQLError.extensions.code,
            errorCode: graphQLError.extensions.errorCode,
            errorSource: graphQLError.extensions.errorSource
          };
          
          console.log('DIAGNOSTIC: ShipStation error details:', errorDetails);
        }
      } else if (error.networkError) {
        errorMessage = 'Network error getting shipping rates. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      this.$emit('error', errorMessage);
      this.validationErrors = [errorMessage];
    } finally {
      this.loadingRates = false;
    }
  }

  async fetchTargetedRate() {
    if (!this.packageFormValid || !this.defaultCarrierCode || !this.defaultServiceCode) {
      console.log('[ShippingLabelForm] Cannot fetch targeted rate - missing requirements:', {
        packageFormValid: this.packageFormValid,
        carrierCode: this.defaultCarrierCode,
        serviceCode: this.defaultServiceCode
      });
      return null;
    }

    console.log('[ShippingLabelForm] Fetching targeted rate:', {
      carrierCode: this.defaultCarrierCode,
      serviceCode: this.defaultServiceCode,
      orderId: this.orderId,
      isMultiPackageMode: this.isMultiPackageMode,
      packageCount: this.packages.length
    });

    this.loadingTargetedRate = true;
    this.carrierErrors = [];
    try {
      let query;
      let queryVariables;

      if (this.isMultiPackageMode) {
        // Multi-package mode: build multi-package input
        // Filter out empty packages (created via auto-advance)
        const validPackages = this.getValidPackages();

        if (validPackages.length === 0) {
          console.warn('[ShippingLabelForm] No valid packages for targeted rate');
          this.loadingTargetedRate = false;
          return;
        }

        console.log('MULTI_PACKAGE_DEBUG: Fetching targeted rate with multi-package support:', {
          totalPackages: this.packages.length,
          validPackages: validPackages.length,
          packages: validPackages.map((pkg, index) => ({
            index: index + 1,
            weight: pkg.weight,
            dimensions: `${pkg.length}x${pkg.width}x${pkg.height}`
          }))
        });

        const multiPackageDetailsInput = this.buildMultiPackageInput(validPackages);

        query = GET_TARGETED_SHIPPING_RATE_MULTI_PACKAGE;
        queryVariables = {
          orderId: this.orderId,
          pickTicketId: this.pickTicketId || null,
          multiPackageDetails: multiPackageDetailsInput,
          carrierCode: this.defaultCarrierCode,
          serviceCode: this.defaultServiceCode,
          emails: this.getEmailsAsString() || null,
          labelMessages: this.getLabelMessagesInput(),
          shipToOverride: this.shipToOverrideInput
        };
      } else {
        // Single package mode (backward compatible)
        console.log('MULTI_PACKAGE_DEBUG: Fetching targeted rate with single-package support');

        const packageDetailsInput = {
          weight: this.packageDetails.weight,
          length: this.packageDetails.length,
          width: this.packageDetails.width,
          height: this.packageDetails.height
        };

        query = GET_TARGETED_SHIPPING_RATE;
        queryVariables = {
          orderId: this.orderId,
          pickTicketId: this.pickTicketId || null,
          packageDetails: packageDetailsInput,
          carrierCode: this.defaultCarrierCode,
          serviceCode: this.defaultServiceCode,
          emails: this.getEmailsAsString() || null,
          labelMessages: this.getLabelMessagesInput(),
          shipToOverride: this.shipToOverrideInput
        };
      }

      console.log('[ShippingLabelForm] About to call targeted rate query with variables:', JSON.stringify(queryVariables, null, 2));

      const response = await this.$apollo.query({
        query,
        variables: queryVariables,
        fetchPolicy: 'network-only'
      });

      console.log('[ShippingLabelForm] Targeted rate response:', response.data);

      const targetedData = response.data.targetedShippingRate;
      if (targetedData && targetedData.rate) {
        this.carrierErrors = [];
        this.targetedRate = targetedData.rate;
        this.hasTargetedRate = true;
        this.shipmentId = targetedData.shipmentId; // Set shipment ID for label creation

        // Save the package details used for this successful fetch
        if (this.isMultiPackageMode) {
          this.lastFetchedPackages = JSON.parse(JSON.stringify(this.packages));
        } else {
          this.lastFetchedPackageDetails = { ...this.packageDetails };
        }

        // SNAPSHOT: Save the label messages and emails used for this shipment creation
        this.lastFetchedLabelMessages = {
          reference1: this.labelComment1 || null,
          reference2: this.labelComment2 || null,
          reference3: this.labelComment3 || null
        };
        this.lastFetchedEmails = this.getEmailsAsString() || null;
        this.usedTargetedRateFetch = true; // Mark that we used targeted rate fetch
        console.log('[ShippingLabelForm] Snapshotted targeted rate context:', {
          shipmentId: this.shipmentId,
          labelMessages: this.lastFetchedLabelMessages,
          emails: this.lastFetchedEmails,
          usedTargetedRateFetch: true
        });

        // Add the targeted rate to the rates array for UI display
        this.rates = [targetedData.rate];

        // Auto-select this rate immediately
        this.selectedRateId = targetedData.rate.rateId;
        this.hasAutoSelectedRate = true;

        console.log('[ShippingLabelForm] Targeted rate fetched and auto-selected:', {
          rateId: targetedData.rate.rateId,
          carrierCode: targetedData.rate.carrierCode,
          serviceCode: targetedData.rate.serviceCode,
          amount: targetedData.rate.shippingAmount?.amount,
          fallbackUsed: targetedData.fallbackUsed
        });

        // Log fallback info if used
        if (targetedData.fallbackUsed) {
          console.log('[ShippingLabelForm] Fallback was used for targeted rate:', targetedData.searchCriteria);
        }

        return targetedData.rate;
      }

      // No rate available — store carrier-specific errors for inline display
      this.carrierErrors = targetedData?.errors || [];

      return null;
    } catch (error: any) {
      console.error('[ShippingLabelForm] Error fetching targeted rate:', error);
      
      // Check if this is a GraphQL schema error (field doesn't exist)
      if (error.graphQLErrors && error.graphQLErrors.some((e: any) => 
        e.message.includes('does not exist on the type') || 
        e.message.includes('getTargetedShippingRate')
      )) {
        console.log('[ShippingLabelForm] GraphQL schema not updated - falling back to full rate fetch');
      }
      
      // Don't show error to user - this is just an optimization
      // Fall back to regular rate fetching
      return null;
    } finally {
      this.loadingTargetedRate = false;
    }
  }

  async fetchBackgroundRates() {
    // Prevent multiple simultaneous background rate fetches
    if (this.loadingBackgroundRates) {
      console.log('[ShippingLabelForm] Background rate fetch already in progress, skipping');
      return;
    }

    console.log('[ShippingLabelForm] Starting background rate fetch for "See All Rates":', {
      isMultiPackageMode: this.isMultiPackageMode,
      packageCount: this.packages.length
    });
    this.loadingBackgroundRates = true;

    try {
      let query;
      let queryVariables;

      if (this.isMultiPackageMode) {
        // Multi-package mode: build multi-package input for background rates
        // Filter out empty packages (created via auto-advance)
        const validPackages = this.getValidPackages();

        if (validPackages.length === 0) {
          console.warn('[ShippingLabelForm] No valid packages for background rates');
          this.loadingBackgroundRates = false;
          return;
        }

        console.log('MULTI_PACKAGE_DEBUG: Fetching background rates with multi-package support:', {
          totalPackages: this.packages.length,
          validPackages: validPackages.length,
          packages: validPackages.map((pkg, index) => ({
            index: index + 1,
            weight: pkg.weight,
            dimensions: `${pkg.length}x${pkg.width}x${pkg.height}`
          }))
        });

        const multiPackageDetailsInput = this.buildMultiPackageInput(validPackages);

        query = GET_SHIPPING_RATES_MULTI_PACKAGE;
        queryVariables = {
          orderId: this.orderId,
          pickTicketId: this.pickTicketId,
          multiPackageDetails: multiPackageDetailsInput,
          emails: this.getEmailsAsString() || null,
          labelMessages: this.getLabelMessagesInput(),
          includeAllCarriers: true, // Get rates from all carriers for "See All Rates" view
          shipToOverride: this.shipToOverrideInput
        };
      } else {
        // Single package mode (backward compatible)
        console.log('MULTI_PACKAGE_DEBUG: Fetching background rates with single-package support');

        const packageDetailsInput = {
          weight: this.packageDetails.weight,
          length: this.packageDetails.length,
          width: this.packageDetails.width,
          height: this.packageDetails.height
        };

        query = GET_SHIPPING_RATES;
        queryVariables = {
          orderId: this.orderId,
          pickTicketId: this.pickTicketId,
          packageDetails: packageDetailsInput,
          emails: this.getEmailsAsString() || null,
          labelMessages: this.getLabelMessagesInput(),
          includeAllCarriers: true, // Get rates from all carriers for "See All Rates" view
          shipToOverride: this.shipToOverrideInput
        };
      }

      const ratesResponse = await this.$apollo.query({
        query,
        variables: queryVariables,
        fetchPolicy: 'network-only'
      });

      const shippingRatesData = ratesResponse.data.shippingRates;

      // Store rates in background array - don't disturb current UI
      this.backgroundRates = shippingRatesData.rates || [];

      console.log('[ShippingLabelForm] Background rate fetch completed:', this.backgroundRates.length, 'rates');

      // Only update the shipment ID if we don't have one yet
      if (!this.shipmentId && shippingRatesData.shipmentId) {
        this.shipmentId = shippingRatesData.shipmentId;
      }
    } catch (error) {
      console.error('[ShippingLabelForm] Background rate fetch failed:', error);
      // Don't show error to user since this is background operation
      this.backgroundRates = [];
    } finally {
      this.loadingBackgroundRates = false;
    }
  }
  
  async fetchOrderShippingMethod() {
    try {
      console.log('[ShippingLabelForm] Fetching order shipping method for order:', this.orderId, 'pickTicketId:', this.pickTicketId, 'isShipperMode:', this.isShipperMode);

      // Use pick ticket query when pickTicketId is available (works for both admin and shipper)
      // This query returns lineItems with styleCode for label reference, plus salesRepId and shipping method
      if (this.pickTicketId) {
        console.log('[ShippingLabelForm] Using pick ticket query for pick ticket:', this.pickTicketId);

        const shipperResponse = await this.$apollo.query({
          query: GET_SHIPPER_ORDER,
          variables: { pickTicketId: this.pickTicketId },
          fetchPolicy: 'network-only'
        });

        console.log('[ShippingLabelForm] Pick ticket query response:', {
          data: shipperResponse.data,
          errors: shipperResponse.errors,
          shipperOrder: shipperResponse.data?.shipperOrder
        });

        const shipperOrder = shipperResponse.data?.shipperOrder;
        if (!shipperOrder) {
          console.warn('[ShippingLabelForm] Pick ticket query returned null - pick ticket may not exist or have line items');
        }
        if (shipperOrder) {
          // Store order data from pick ticket query
          this.orderData = {
            orderNumber: shipperOrder.orderNumber,
            customerName: shipperOrder.customerName || 'Unknown Customer',
            salesRepId: shipperOrder.salesRepId || null, // For label reference (sales rep initials)
            createdBy: shipperOrder.createdBy || null,
            customerEmail: shipperOrder.customerEmail || shipperOrder.shippingAddress?.email || null,
            shippingAddress: shipperOrder.shippingAddress || null
          };

          // Extract first line item style code for label reference
          if (shipperOrder.lineItems?.length > 0) {
            this.firstLineItemStyleCode = shipperOrder.lineItems[0].styleCode || '';
          }

          console.log('[ShippingLabelForm] Pick ticket order context loaded:', {
            orderNumber: this.orderData.orderNumber,
            customerName: this.orderData.customerName,
            customerEmail: this.orderData.customerEmail,
            salesRepId: this.orderData.salesRepId,
            firstLineItemStyleCode: this.firstLineItemStyleCode
          });

          // Set the customer contact email for UI display
          this.customerContactEmail = this.orderData.customerEmail;

          // Fetch emails from all sources concurrently (same as standard path)
          const emailPromises: Promise<void>[] = [];

          if (this.supportsMultipleNotificationEmails && shipperOrder.salesRepId) {
            emailPromises.push(this.fetchSalesRepEmail(shipperOrder.salesRepId));
          }

          if (this.supportsMultipleNotificationEmails && this.includeOrderCreatorEmail && shipperOrder.createdBy) {
            emailPromises.push(this.fetchOrderCreatorEmail(shipperOrder.createdBy));
          }

          await Promise.all(emailPromises);

          // Update default emails after all sources are loaded
          this.updateDefaultEmails();

          // Set shipping method from shipper order data
          if (shipperOrder.shippingCarrierCode || shipperOrder.shippingServiceCode) {
            this.orderShippingMethod = {
              shippingMethodName: shipperOrder.shippingMethodName || 'Standard',
              carrierCode: shipperOrder.shippingCarrierCode,
              serviceCode: shipperOrder.shippingServiceCode,
              packageType: null // Not available in shipper query
            };
            this.defaultCarrierCode = shipperOrder.shippingCarrierCode;
            this.defaultServiceCode = shipperOrder.shippingServiceCode;

            console.log('[ShippingLabelForm] Pick ticket shipping method loaded:', {
              method: shipperOrder.shippingMethodName,
              carrier: this.defaultCarrierCode,
              service: this.defaultServiceCode
            });
          } else {
            console.log('[ShippingLabelForm] No shipping method defined for pick ticket order, using standard flow');
          }
        }
        return; // Exit early - pick ticket query handled
      }

      // Standard query for non-shipper users
      const response = await this.$apollo.query({
        query: GET_ORDER_WITH_SHIPPING_METHOD,
        variables: { orderId: this.orderId },
        fetchPolicy: 'network-only'
      });

      const order = response.data?.salesOrderById;
      if (order) {
        // Extract customer contact email from order.customer.customer.contacts (standard contacts)
        const customerEmail = order.customer?.customer?.contacts
          ?.find((contact: any) => contact.primaryContact && contact.isActive && contact.email)?.email
          || order.customer?.customer?.contacts
            ?.find((contact: any) => contact.isActive && contact.email)?.email;

        // Extract customer contact email from quote relationship (QuoteCustomer.ContactId -> Contact.Email)
        const quoteContactEmail = order.quote?.quoteCustomer?.contact?.email;

        // Use quote contact email as priority, fallback to customer contacts
        const finalCustomerEmail = quoteContactEmail || customerEmail;

        // Resolve shipping address from customer addresses
        const addresses = order.customer?.customer?.addresses || [];
        const shipToId = order.customer?.shipTo;
        const shippingAddr = addresses.find((a: any) => a.addressId === shipToId)
            || addresses.find((a: any) => a.isPrimaryAddress)
            || addresses[0] || null;

        // Store complete order data for display and email extraction
        this.orderData = {
          orderNumber: order.orderNumber,
          customerName: order.customer?.customer?.organization || order.customer?.organization || 'Unknown Customer',
          salesRepId: order.salesRepId,
          createdBy: order.createdBy,
          customerEmail: finalCustomerEmail,
          shippingAddress: shippingAddr ? {
            street1: shippingAddr.addressLine1,
            street2: shippingAddr.addressLine2,
            city: shippingAddr.city,
            state: shippingAddr.state,
            postalCode: shippingAddr.postalCode
          } : null
        };

        // Extract first line item style code for label reference
        if (order.lineItems?.length > 0) {
          // Find first line item with a style code
          const firstItemWithStyle = order.lineItems.find((li: any) => li.item?.styleCode);
          this.firstLineItemStyleCode = firstItemWithStyle?.item?.styleCode || '';
        }

        console.log('[ShippingLabelForm] Order context loaded:', {
          orderNumber: this.orderData.orderNumber,
          customerName: this.orderData.customerName,
          customerEmail: this.orderData.customerEmail,
          salesRepId: this.orderData.salesRepId,
          quoteContactEmail: quoteContactEmail,
          firstLineItemStyleCode: this.firstLineItemStyleCode
        });

        // Set the customer contact email for UI display
        this.customerContactEmail = finalCustomerEmail;

        // Fetch emails from all three sources concurrently
        const emailPromises: Promise<void>[] = [];

        // Fetch sales rep email only when ShipStation can deliver to more than one address
        if (this.supportsMultipleNotificationEmails && order.salesRepId) {
          emailPromises.push(this.fetchSalesRepEmail(order.salesRepId));
        }

        // Fetch order creator email if user has opted in
        if (this.supportsMultipleNotificationEmails && this.includeOrderCreatorEmail && order.createdBy) {
          emailPromises.push(this.fetchOrderCreatorEmail(order.createdBy));
        }

        // Wait for all email fetching to complete
        await Promise.all(emailPromises);

        // Update default emails after all sources are loaded
        this.updateDefaultEmails();

        if (order.shippingMethod) {
          this.orderShippingMethod = order.shippingMethod;
          this.defaultCarrierCode = order.shippingMethod.carrierCode;
          this.defaultServiceCode = order.shippingMethod.serviceCode;
          this.defaultPackageType = order.shippingMethod.packageType;

          console.log('[ShippingLabelForm] Order shipping method loaded:', {
            method: order.shippingMethod.shippingMethodName,
            carrier: this.defaultCarrierCode,
            service: this.defaultServiceCode,
            package: this.defaultPackageType
          });
        } else {
          console.log('[ShippingLabelForm] No shipping method defined for order, using standard flow');
        }
      }
    } catch (error) {
      console.error('[ShippingLabelForm] Error fetching order shipping method:', error);
      // Continue without auto-selection - not a critical error
    }
    console.log('[ShippingLabelForm] fetchOrderShippingMethod() function ENDING');
  }

  async fetchSalesRepEmail(salesRepId: number) {
    try {
      console.log('[ShippingLabelForm] Fetching sales rep email for ID:', salesRepId);

      const response = await this.$apollo.query({
        query: queries.LIST_SALES_REPS,
        variables: {
          offset: 0,
          take: 1,
          where: { salesRepId: { eq: salesRepId } }
        },
        fetchPolicy: 'cache-first'
      });

      const salesRep = response.data?.salesReps?.items?.[0];
      if (salesRep?.email) {
        this.salesRepEmail = salesRep.email;
        console.log('[ShippingLabelForm] Sales rep email loaded:', this.salesRepEmail);

        // Update default emails
        this.updateDefaultEmails();
      }
    } catch (error) {
      console.error('[ShippingLabelForm] Error fetching sales rep email:', error);
      // Continue without sales rep email - not a critical error
    }
  }

  async fetchOrderCreatorEmail(userId: number) {
    try {
      const response = await this.$apollo.query({
        query: GET_USER_EMAIL,
        variables: { userId },
        fetchPolicy: 'cache-first'
      });

      const user = response.data?.userById;
      if (user?.email) {
        this.orderCreatorEmail = user.email;
        this.updateDefaultEmails();
      }
    } catch (error) {
      console.error('[ShippingLabelForm] Error fetching order creator email:', error);
    }
  }

  async onIncludeCreatorEmailChange(value: boolean) {
    // Fetch the creator email if toggling on and it hasn't been loaded yet
    if (value && !this.orderCreatorEmail && this.orderData?.createdBy) {
      await this.fetchOrderCreatorEmail(this.orderData.createdBy);
    }

    this.updateDefaultEmails();
  }

  get notificationEmailHint(): string {
    return this.supportsMultipleNotificationEmails
      ? 'Default emails from sales rep and customer are pre-populated'
      : 'Customer email is pre-populated. ShipStation only delivers notifications to a single address.';
  }

  updateDefaultEmails() {
    const emails: string[] = [];

    // Sales rep and order creator are only added when ShipStation can deliver to more than one
    // address (see supportsMultipleNotificationEmails). Otherwise the customer email stands alone.
    if (this.supportsMultipleNotificationEmails && this.salesRepEmail) {
      emails.push(this.salesRepEmail);
    }

    // Add customer email
    if (this.orderData?.customerEmail) {
      emails.push(this.orderData.customerEmail);
    }

    // Add order creator email only if the user has opted in
    if (this.supportsMultipleNotificationEmails && this.includeOrderCreatorEmail && this.orderCreatorEmail) {
      emails.push(this.orderCreatorEmail);
    }

    // Remove duplicates and set as array for v-combobox
    const uniqueEmails = [...new Set(emails)];
    this.notificationEmails = uniqueEmails;

    console.log('[ShippingLabelForm] Default emails updated:', this.notificationEmails);
  }

  trimNotificationEmails() {
    if (Array.isArray(this.notificationEmails)) {
      this.notificationEmails = this.notificationEmails
        .map(e => typeof e === 'string' ? e.trim() : e)
        .filter(e => e);
      // ShipStation drops the notification entirely when given more than one address (#1154),
      // so keep only the most recently entered one until that is fixed upstream.
      if (!this.supportsMultipleNotificationEmails && this.notificationEmails.length > 1) {
        this.notificationEmails = this.notificationEmails.slice(-1);
      }
    }
  }

  removeEmailChip(email: string) {
    if (Array.isArray(this.notificationEmails)) {
      const index = this.notificationEmails.indexOf(email);
      if (index > -1) {
        this.notificationEmails.splice(index, 1);
      }
    }
  }

  getEmailsAsString(): string {
    if (Array.isArray(this.notificationEmails)) {
      return this.notificationEmails.join(', ');
    }
    return typeof this.notificationEmails === 'string' ? this.notificationEmails : '';
  }

  async autoFetchRates() {
    // Check feature flag
    if (!this.isAutoFetchEnabled) {
      console.log('[ShippingLabelForm] Auto-fetch is disabled via feature flag');
      return;
    }

    // Prevent multiple simultaneous auto-fetches
    if (this.isAutoFetching || this.loadingRates || this.loadingTargetedRate) {
      console.log('[ShippingLabelForm] Auto-fetch already in progress, skipping');
      return;
    }

    // Ensure form is validated - Vuetify forms may not validate synchronously on mount
    if (!this.packageFormValid) {
      console.log('[ShippingLabelForm] Form not validated yet, triggering validation...');
      const form = this.$refs.packageForm as any;
      if (form && typeof form.validate === 'function') {
        form.validate();
        // Wait for Vue to process the validation result
        await this.$nextTick();
      }

      // Still invalid after explicit validation - skip auto-fetch
      if (!this.packageFormValid) {
        console.log('[ShippingLabelForm] Package form is invalid after validation, skipping auto-fetch');
        return;
      }
    }
    
    console.log('[ShippingLabelForm] Starting dual-tier auto-fetch of shipping rates');
    console.log('[ShippingLabelForm] Shipping method state:', {
      defaultCarrierCode: this.defaultCarrierCode,
      defaultServiceCode: this.defaultServiceCode,
      defaultPackageType: this.defaultPackageType,
      orderShippingMethod: this.orderShippingMethod?.shippingMethodName || null
    });
    this.isAutoFetching = true;
    this.autoFetchAttempted = true;

    // Strategy 1: Try fast targeted rate first (if we have shipping method info)
    if (this.defaultCarrierCode && this.defaultServiceCode) {
      console.log('[ShippingLabelForm] Attempting fast targeted rate fetch');
      
      try {
        const targetedRate = await this.fetchTargetedRate();
        
        if (targetedRate && this.hasTargetedRate) {
          console.log('[ShippingLabelForm] ⚡ Fast targeted rate successful! Ready for user to proceed.');
          
          // Mark auto-fetching as complete since we have what we need
          this.isAutoFetching = false;
          
          // Start background fetching of all rates for "See All Rates" option
          // This runs completely independently and doesn't block the user
          setTimeout(() => {
            this.fetchBackgroundRates().catch(error => {
              console.error('[ShippingLabelForm] Background rate fetch failed:', error);
            });
          }, 100); // Small delay to ensure UI state is updated
          
          return; // Success path - user can proceed immediately
        } else {
          console.log('[ShippingLabelForm] Fast targeted rate failed - falling back to full rate fetch');
        }
      } catch (error) {
        console.error('[ShippingLabelForm] Targeted rate fetch error:', error);
        console.log('[ShippingLabelForm] Falling back to full rate fetch');
      }
    } else {
      console.log('[ShippingLabelForm] No shipping method info available - using full rate fetch');
    }
    
    // Strategy 2: Non-blocking fallback - prepare for user to proceed while rates load in background
    console.log('[ShippingLabelForm] ⚡ Ready for user to proceed - rates will load in background');
    
    // Mark auto-fetching as complete - user can now click "Next"
    this.isAutoFetching = false;
    
    // Start background rate loading without blocking the user
    this.fetchBackgroundRates().then(() => {
      console.log('[ShippingLabelForm] Background rates loaded:', this.backgroundRates.length, 'rates');
      // Background rates are now cached and ready for when user clicks "View All Rates"
      // Do NOT automatically populate the UI - wait for explicit user action
    }).catch(error => {
      console.error('[ShippingLabelForm] Background rate fetch failed:', error);
      // Show manual "Get Rates" button as fallback
      this.showManualRateButton = true;
    });
  }
  
  autoSelectDefaultRate() {
    if (!this.rates.length || this.hasAutoSelectedRate) {
      console.log('[ShippingLabelForm] Skipping auto-selection:', {
        ratesCount: this.rates.length,
        hasAutoSelected: this.hasAutoSelectedRate
      });
      return;
    }
    
    let selectedRate: Rate | null = null;
    
    // DEBUG: Log all available service codes for the default carrier
    if (this.defaultCarrierCode) {
      const carrierRates = this.rates.filter(r => r.carrierCode === this.defaultCarrierCode);
      console.log('[ShippingLabelForm] DEBUG - Available service codes for carrier', this.defaultCarrierCode + ':', 
        carrierRates.map(r => ({
          serviceCode: r.serviceCode,
          serviceName: r.serviceName,
          cost: r.shippingAmount.amount
        }))
      );
      console.log('[ShippingLabelForm] DEBUG - Looking for service code:', this.defaultServiceCode);
    }
    
    // Priority 1: Exact match on carrier and service
    if (this.defaultCarrierCode && this.defaultServiceCode) {
      selectedRate = this.rates.find(r => {
        const matches = r.carrierCode === this.defaultCarrierCode &&
                       r.serviceCode === this.defaultServiceCode;
        if (r.carrierCode === this.defaultCarrierCode) {
          console.log('[ShippingLabelForm] DEBUG - Service code comparison:', {
            expected: this.defaultServiceCode,
            actual: r.serviceCode,
            matches: r.serviceCode === this.defaultServiceCode
          });
        }
        return matches;
      }) || null;
      
      if (selectedRate) {
        console.log('[ShippingLabelForm] Auto-selected exact match:', {
          carrier: selectedRate.carrierCode,
          service: selectedRate.serviceCode
        });
      }
    }
    
    // Priority 2: Match carrier only, select cheapest service
    if (!selectedRate && this.defaultCarrierCode) {
      const carrierRates = this.rates.filter(r => 
        r.carrierCode === this.defaultCarrierCode
      );
      
      if (carrierRates.length > 0) {
        selectedRate = carrierRates.reduce((min, r) => 
          r.shippingAmount.amount < min.shippingAmount.amount ? r : min
        );
        
        if (selectedRate) {
          console.log('[ShippingLabelForm] Auto-selected cheapest for carrier:', {
            carrier: selectedRate.carrierCode,
            service: selectedRate.serviceCode,
            cost: selectedRate.shippingAmount.amount
          });
        }
      }
    }
    
    // Priority 3: Use ShippingRateAnalyzer recommendation
    if (!selectedRate) {
      // Initialize analyzer if needed
      if (!this.rateAnalyzer && this.rates.length > 0) {
        const orderContext: OrderContext = {
          orderId: this.orderId,
          destinationZone: this.rates[0]?.zone
        };
        
        this.rateAnalyzer = new ShippingRateAnalyzer(
          this.rates,
          this.userPreferences || undefined,
          orderContext
        );
        
        this.analyzedRates = this.rateAnalyzer.analyzeRates();
      }
      
      if (this.analyzedRates && this.analyzedRates.length > 0) {
        // Find the best analyzed rate (first one is typically the best/recommended)
        const bestAnalyzedRate = this.analyzedRates.find(ar => 
          ar.analysis?.tags?.primary === PrimaryTag.RECOMMENDED || 
          ar.analysis?.tags?.primary === PrimaryTag.BEST_OVERALL
        ) || this.analyzedRates[0];
        
        if (bestAnalyzedRate) {
          selectedRate = this.rates.find(r => 
            r.rateId === bestAnalyzedRate.rateId
          ) || null;
          
          if (selectedRate) {
            console.log('[ShippingLabelForm] Auto-selected analyzer recommendation:', {
              carrier: selectedRate.carrierCode,
              service: selectedRate.serviceCode,
              reason: 'analyzer recommendation',
              primaryTag: bestAnalyzedRate.analysis?.tags?.primary
            });
          }
        }
      }
    }
    
    // Priority 4: Select cheapest overall rate
    if (!selectedRate && this.rates.length > 0) {
      selectedRate = this.rates.reduce((min, r) => 
        r.shippingAmount.amount < min.shippingAmount.amount ? r : min
      );
      
      if (selectedRate) {
        console.log('[ShippingLabelForm] Auto-selected cheapest overall:', {
          carrier: selectedRate.carrierCode,
          service: selectedRate.serviceCode,
          cost: selectedRate.shippingAmount.amount
        });
      }
    }
    
    if (selectedRate) {
      this.selectedRateId = selectedRate.rateId;
      this.hasAutoSelectedRate = true;
      console.log('[ShippingLabelForm] Rate auto-selected successfully:', selectedRate.rateId);
    } else {
      console.log('[ShippingLabelForm] No rate could be auto-selected');
    }
  }
  
  showAllRatesView() {
    console.log('[ShippingLabelForm] User requested to see all rates');
    
    // If background rates are available, use them
    if (this.backgroundRates.length > 0) {
      console.log('[ShippingLabelForm] Using cached background rates:', this.backgroundRates.length);
      // Replace the current rates with the full set
      this.rates = [...this.backgroundRates];
      
      // Initialize rate analyzer for the full set of rates
      const orderContext: OrderContext = {
        orderId: this.orderId,
        destinationZone: this.rates[0]?.zone
      };
      
      this.rateAnalyzer = new ShippingRateAnalyzer(
        this.rates,
        this.userPreferences || undefined,
        orderContext
      );
      this.analyzedRates = this.rateAnalyzer.analyzeRates();
      
      // Clear the auto-selection flags to show rate selection
      this.hasAutoSelectedRate = false;
      this.hasTargetedRate = false;
      
      // Don't immediately show all rates - show featured view first
      this.showAllRates = false;
      return;
    }
    
    // If we only have the targeted rate and background loading hasn't completed, wait for it
    if (this.hasTargetedRate && this.rates.length <= 1 && !this.loadingBackgroundRates) {
      console.log('[ShippingLabelForm] Background rates not loaded yet, starting fetch');
      // Trigger background rates fetch if it hasn't been started
      this.fetchBackgroundRates().then(() => {
        console.log('[ShippingLabelForm] Background rates loaded, showing all rates view');
        
        // Use the background rates that were just fetched
        if (this.backgroundRates.length > 0) {
          this.rates = [...this.backgroundRates];
          
          // Initialize rate analyzer for the full set of rates
          const orderContext: OrderContext = {
            orderId: this.orderId,
            destinationZone: this.rates[0]?.zone
          };
          
          this.rateAnalyzer = new ShippingRateAnalyzer(
            this.rates,
            this.userPreferences || undefined,
            orderContext
          );
          this.analyzedRates = this.rateAnalyzer.analyzeRates();
        }
        
        this.hasAutoSelectedRate = false;
        this.hasTargetedRate = false;
        this.showAllRates = false; // Show featured rates first
      }).catch(error => {
        console.error('[ShippingLabelForm] Failed to load background rates:', error);
        // Still show what we have
        this.showAllRates = false; // Show featured rates first
      });
      return;
    }
    
    // If background rates are still loading, just wait (the UI will show loading state)
    if (this.loadingBackgroundRates) {
      console.log('[ShippingLabelForm] Background rates are loading, waiting...');
      return;
    }
    
    // Clear the auto-selection flags to show rate selection
    this.hasAutoSelectedRate = false;
    this.hasTargetedRate = false;
    this.showAllRates = false; // Show featured rates first
  }
  
  async generateLabel() {
    // Prevent multiple rapid clicks
    if (this.generatingLabel) {
      console.log('[ShippingLabelForm] Label generation already in progress, ignoring click');
      return;
    }

    this.generatingLabel = true;
    try {
      // AUTO-REFETCH: Check if label messages or emails have changed since shipment was created
      if (this.hasShipmentContextChanged()) {
        console.log('[ShippingLabelForm] Label messages or emails changed, automatically refreshing rates with updated context...');

        // Remember if user had manually overridden the shipping method before we clear state
        const hadUserOverride = this.userOverrideRate ? { ...this.userOverrideRate } : null;

        // Clear the cached shipment and rates to force new shipment creation
        this.shipmentId = null;
        this.selectedRateId = null;
        this.rates = [];
        this.hasTargetedRate = false;
        this.hasAutoSelectedRate = false;

        // Re-fetch rates with updated context using the same method as before
        if (this.usedTargetedRateFetch) {
          if (hadUserOverride) {
            // User manually overrode the shipping method - fetch ALL rates so we can
            // re-select their chosen service instead of reverting to the order default
            console.log('[ShippingLabelForm] Re-fetching all rates to preserve user override:', hadUserOverride);
            await this.getRates();

            if (!this.shipmentId) {
              this.validationErrors.push('Failed to refresh rates with updated label messages/emails. Please try again.');
              this.generatingLabel = false;
              return;
            }

            // Try to re-select the user's overridden service from the new rates
            const matchingRate = this.rates.find(r =>
              r.carrierCode === hadUserOverride.carrierCode &&
              r.serviceCode === hadUserOverride.serviceCode
            );
            if (matchingRate) {
              this.selectedRateId = matchingRate.rateId;
              this.userOverrideRate = hadUserOverride; // Restore the override tracking
              console.log('[ShippingLabelForm] Re-selected user override rate:', matchingRate.rateId);
            } else {
              // Override service no longer available - let user pick again
              console.warn('[ShippingLabelForm] User override service not found in refreshed rates, user must re-select');
              this.userOverrideRate = null;
              this.validationErrors.push('Your previously selected shipping service is no longer available. Please select a new one.');
              this.generatingLabel = false;
              return;
            }
          } else {
            // No user override - use targeted rate fetch as before
            console.log('[ShippingLabelForm] Re-fetching using targeted rate (same as initial fetch)');
            await this.fetchTargetedRate();

            // Check if targeted rate fetch was successful
            if (!this.shipmentId || !this.selectedRateId) {
              this.validationErrors.push('Failed to refresh targeted rate with updated label messages/emails. Please try again.');
              this.generatingLabel = false;
              return;
            }
          }
        } else {
          // We previously used regular rate fetch - use it again
          console.log('[ShippingLabelForm] Re-fetching using regular rates (same as initial fetch)');
          await this.getRates();

          // For regular rate fetch, we don't auto-select a rate, so just check shipmentId
          if (!this.shipmentId) {
            this.validationErrors.push('Failed to refresh rates with updated label messages/emails. Please try clicking "Get Rates" manually.');
            this.generatingLabel = false;
            return;
          }

          // If we have rates but no selected rate, the user needs to select one
          if (!this.selectedRateId && this.rates.length > 0) {
            this.validationErrors.push('Please select a shipping rate from the updated list before generating a label.');
            this.generatingLabel = false;
            return;
          }
        }

        console.log('[ShippingLabelForm] Rates refreshed successfully with updated context, proceeding with label generation');
      }

      console.log('DIAGNOSTIC: Creating shipping label:', {
        orderId: this.orderId,
        pickTicketId: this.pickTicketId,
        rateId: this.selectedRateId,
        shipmentId: this.shipmentId,
        testMode: this.testMode,
        timestamp: new Date().toISOString()
      });
      
      const response = await this.$apollo.mutate({
        mutation: CREATE_SHIPPING_LABEL,
        variables: {
          orderId: this.orderId,
          pickTicketId: this.pickTicketId || null,
          rateId: this.selectedRateId,
          shipmentId: this.shipmentId,
          testMode: this.testMode,
          labelFormat: this.labelFormat,
          labelLayout: this.labelLayout,
          emails: this.getEmailsAsString(),
          includeReturnLabel: this.createReturnLabel,
          returnLabelMessages: this.getReturnLabelMessagesInput(),
          returnLabelPackageSequences: this.createReturnLabel && this.isMultiPackageMode
            ? this.returnLabelPackageSelections
            : null,
          returnLabelRmaPerPackage: this.getReturnLabelRmaPerPackageInput()
        }
      });

      console.log('DIAGNOSTIC: Create label GraphQL response:', {
        data: response.data,
        timestamp: new Date().toISOString()
      });

      const labelData = response.data.createShippingLabel;

      // Auto-print label(s) if enabled and printer is connected
      if (this.autoPrintEnabled && this.selectedPrinterId && this.hardwareService && labelData.label) {
        try {
          let outboundPrintCount = 0;
          let returnPrinted = false;

          // Use allLabels for multi-package shipments, fallback to single label for backward compatibility
          const labelsToPrint = labelData.allLabels && labelData.allLabels.length > 0
            ? labelData.allLabels.filter((l: any) => !l.isReturnLabel)
            : [labelData.label];

          const isMultiPackage = labelData.isMultiPackage || labelsToPrint.length > 1;

          if (isMultiPackage) {
            console.log(`MULTI_PACKAGE: Printing ${labelsToPrint.length} package labels`);
          }

          // Print all outbound labels (for multi-package shipments)
          for (let i = 0; i < labelsToPrint.length; i++) {
            const label = labelsToPrint[i];
            const packageInfo = isMultiPackage ? ` (Package ${label.packageSequence || i + 1}/${label.totalPackages || labelsToPrint.length})` : '';

            // Check if we have a PDF URL (ShipStation V2 returns URL)
            if (label.labelData && label.labelData.startsWith('http')) {
              // URL label: served by TL-OES via its id (the mutation result always carries shippingLabelId);
              // auto-print always prints each package's own PDF. Raw ZPL/EPL payloads take the else-if below.
              console.log(`Auto-printing outbound label${packageInfo} (shippingLabelId ${label.shippingLabelId})`);
              const printResult = await printStoredLabel(this.hardwareService, this.selectedPrinterId, label, false);

              if (printResult.success) {
                console.log(`Outbound label${packageInfo} auto-printed successfully`);
                outboundPrintCount++;
              } else {
                console.error(`Auto-print failed for outbound label${packageInfo}:`, printResult.error);
                this.validationErrors.push(`Auto-print failed${packageInfo}: ${printResult.error}`);
              }
            } else if (label.labelData) {
              // Legacy raw data printing (ZPL, EPL, etc.)
              let printFormat: 'zpl' | 'epl' | 'raw' = 'raw';

              if (label.labelData.startsWith('^XA') || label.labelData.startsWith('~CT')) {
                printFormat = 'zpl';
              } else if (label.labelData.startsWith('N\n')) {
                printFormat = 'epl';
              }

              const jobId = await this.hardwareService.printLabel(
                this.selectedPrinterId,
                label.labelData,
                printFormat
              );
              console.log(`Outbound label${packageInfo} print job submitted: ${jobId}`);
              outboundPrintCount++;
            }

            // Small delay between labels to prevent printer buffer overflow
            if (isMultiPackage && i < labelsToPrint.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }

          // Print return label(s) if created
          const returnLabelsToprint = labelData.returnLabels || (labelData.returnLabel ? [labelData.returnLabel] : []);
          for (let r = 0; r < returnLabelsToprint.length; r++) {
            const rl = returnLabelsToprint[r];
            const returnPkgInfo = returnLabelsToprint.length > 1 ? ` (package ${rl.packageSequence || r + 1})` : '';
            console.log(`Return label${returnPkgInfo} detected, attempting to auto-print`);

            if (rl.labelData && rl.labelData.startsWith('http')) {
              const returnPrintResult = await printStoredLabel(this.hardwareService, this.selectedPrinterId, rl, false);

              if (returnPrintResult.success) {
                console.log(`Return label${returnPkgInfo} auto-printed successfully`);
                returnPrinted = true;
              } else {
                console.error(`Auto-print failed for return label${returnPkgInfo}:`, returnPrintResult.error);
                this.validationErrors.push(`Return label${returnPkgInfo} auto-print failed: ${returnPrintResult.error}`);
              }
            } else if (rl.labelData) {
              let printFormat: 'zpl' | 'epl' | 'raw' = 'raw';
              if (rl.labelData.startsWith('^XA') || rl.labelData.startsWith('~CT')) {
                printFormat = 'zpl';
              } else if (rl.labelData.startsWith('N\n')) {
                printFormat = 'epl';
              }
              const returnJobId = await this.hardwareService.printLabel(
                this.selectedPrinterId,
                rl.labelData,
                printFormat
              );
              console.log(`Return label${returnPkgInfo} print job submitted: ${returnJobId}`);
              returnPrinted = true;
            }

            // Small delay between return labels
            if (r < returnLabelsToprint.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }

          if (outboundPrintCount === 0) {
            console.log('No printable outbound label data found');
          } else if (isMultiPackage) {
            console.log(`MULTI_PACKAGE: Successfully printed ${outboundPrintCount}/${labelsToPrint.length} package labels`);
          }
          if (returnLabelsToprint.length > 0 && !returnPrinted) {
            console.log('No printable return label data found');
          }
        } catch (error) {
          console.error('Failed to auto-print label:', error);
          // Show warning but don't fail the whole operation if printing fails
          this.validationErrors = ['Label created but auto-print failed. Please print manually.'];
        }
      }
      
      // Show warnings (e.g., return label failed but primary succeeded)
      if (labelData.warnings && labelData.warnings.length > 0) {
        for (const warning of labelData.warnings) {
          console.warn('[ShippingLabelForm] Server warning:', warning);
          this.$toast?.warning(warning, { timeout: 8000 });
        }
      }

      // Store the label data and close immediately — success feedback is
      // handled by the GlobalShippingDialog snackbar (persists after close).
      this.createdLabelData = labelData;
      this.$emit('label-created', this.createdLabelData);
      this.$emit('close');
    } catch (error: any) {
      console.error('DIAGNOSTIC: Error creating label:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
        timestamp: new Date().toISOString()
      });

      // Extract error details from GraphQL response
      let errorMessage = 'Error creating shipping label';
      let errorDetails = null;

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        errorMessage = graphQLError.message;

        // Check for ShipStation specific errors with additional details
        if (graphQLError.extensions) {
          errorDetails = {
            code: graphQLError.extensions.code,
            errorCode: graphQLError.extensions.errorCode,
            errorSource: graphQLError.extensions.errorSource
          };

          console.log('DIAGNOSTIC: ShipStation error details:', errorDetails);

          // Display user-friendly message for carrier configuration errors
          if (errorDetails.errorCode === 'CARRIER_CONFIG_ERROR') {
            errorMessage = `Carrier Account Setup Required\n\n${errorMessage}\n\nPlease visit your ShipStation account to:\n• Add billing information for the carrier\n• Configure carrier account credentials\n• Ensure sufficient account balance`;
          }
        }
      } else if (error.networkError) {
        errorMessage = 'Network error creating shipping label. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      this.$emit('error', errorMessage);
      this.validationErrors = [errorMessage];
    } finally {
      this.generatingLabel = false;
    }
  }
  
  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString();
  }

  formatShortDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  goBackToStep1() {
    this.currentStep = 1;
    this.validationErrors = [];
  }
  
  goBack() {
    // If we're in step 2 and showing all rates, go back to featured rates first
    if (this.currentStep === 2 && this.showAllRates) {
      this.showAllRates = false;
      return;
    }

    // For shipper users, skip step 2 (rate selection) entirely
    if (this.isShipperUser && this.currentStep === 3) {
      this.currentStep = 1;
      this.validationErrors = [];
      this.showAllRates = false;
      return;
    }

    // Otherwise, go back to previous step
    if (this.currentStep > 1) {
      this.currentStep--;
      this.validationErrors = [];

      // Reset the rates view when going back from step 2
      if (this.currentStep === 1) {
        this.showAllRates = false;
      }
    }
  }

  skipToManualRateEntry() {
    console.log('[ShippingLabelForm] User chose to skip automatic rate loading');
    
    // Stop any background loading
    this.loadingBackgroundRates = false;
    this.loadingRates = false;
    
    // Reset auto-fetch flags
    this.isAutoFetching = false;
    this.hasTargetedRate = false;
    this.hasAutoSelectedRate = false;
    
    // Clear background rates
    this.backgroundRates = [];
    
    // Show manual get rates button
    this.showManualRateButton = true;
    
    // Go back to step 1 where they can manually trigger rate fetching
    this.currentStep = 1;
  }
  
  // Hardware Integration Methods
  async initializeHardware() {
    console.log('[ShippingLabelForm] Initializing hardware integration...');
    
    // Wrap in try-catch and use setTimeout to prevent blocking
    setTimeout(async () => {
      try {
        // Get hardware service (will load settings from backend)
        this.hardwareService = getHardwareService();
        console.log('[ShippingLabelForm] Got hardware service instance');

        // Set up event listeners before initialize so we don't miss events
        this.hardwareService.on('weight', this.handleWeightUpdate);
        this.hardwareService.on('connected', this.handleHardwareConnected);
        this.hardwareService.on('disconnected', this.handleHardwareDisconnected);
        this.hardwareService.on('scale-error', this.handleScaleError);
        this.hardwareService.on('devices-updated', this.handleDevicesUpdated);
        this.hardwareService.on('scale-connected', this.handleScaleConnected);
        this.hardwareService.on('printer-connected', this.handlePrinterConnected);
        this.hardwareService.on('scanner-connected', this.handleScannerConnected);
        this.hardwareService.on('package-scan', this.handlePackageScan);

        // Initialize the service first (loads settings, connects WebSocket/HTTP)
        await this.hardwareService.initialize();

        // Now check availability after settings are loaded and connection is established
        const availability = await this.hardwareService.checkAvailability();
        console.log('[ShippingLabelForm] Hardware availability:', availability);
        this.hardwareAvailable = availability.isAvailable;

        if (this.hardwareAvailable) {
          console.log('[ShippingLabelForm] Hardware is available, setting up...');

          // Discover available devices
          await this.discoverHardware();
          
          // Load saved printer preference
          const savedPrinterId = localStorage.getItem('shipping_label_printer_id');
          const savedAutoPrint = localStorage.getItem('shipping_label_auto_print');
          
          if (savedPrinterId && this.availablePrinters.some(p => p.id === savedPrinterId)) {
            this.selectedPrinterId = savedPrinterId;
            // Try to connect to the saved printer
            const printer = this.availablePrinters.find(p => p.id === savedPrinterId);
            if (printer) {
              this.handlePrinterSelection(printer);
            }
          }
          
          if (savedAutoPrint !== null) {
            this.autoPrintEnabled = savedAutoPrint === 'true';
          }
        } else {
          console.log('[ShippingLabelForm] Hardware not available:', availability.error);
        }
      } catch (error) {
        console.error('[ShippingLabelForm] Error initializing hardware:', error);
        this.hardwareAvailable = false;
      }
    }, 100); // Small delay to prevent blocking
  }
  
  async discoverHardware() {
    if (!this.hardwareService || !this.hardwareAvailable) return;
    
    try {
      // Discover scales
      const scales = await this.hardwareService.discoverScales();
      if (scales.length > 0) {
        this.availableScales = scales;
        
        // Auto-connect to first available scale if none connected
        if (!this.connectedScale) {
          const firstScale = scales[0];
          console.log('[ShippingLabelForm] Auto-connecting to first available scale:', firstScale.manufacturer, firstScale.product);
          // Use a small delay to ensure UI is ready
          setTimeout(() => {
            this.connectToScale(firstScale.id);
          }, 100);
        }
      }
      
      // Discover printers
      const printers = await this.hardwareService.discoverPrinters();
      if (printers.length > 0) {
        this.availablePrinters = printers;
      }
      
      // Note: Auto-connection is now handled by the HardwareIntegrationService
      // when it receives the device list from UHB
    } catch (error) {
      console.error('Error discovering hardware:', error);
    }
  }
  
  handleScaleSelection(scale: Scale | null) {
    if (scale && scale.id) {
      this.connectToScale(scale.id);
    }
  }
  
  handlePrinterSelection(printer: Printer | null) {
    if (printer && printer.id) {
      this.selectedPrinterId = printer.id;
      this.connectToPrinter(printer.id);
    }
  }
  
  async connectToScale(scaleId: string) {
    if (!this.hardwareService) return;
    
    try {
      await this.hardwareService.connectToScale(scaleId);
      this.connectedScale = this.availableScales.find(s => s.id === scaleId) || null;
      this.hardwareError = null;
      
      // Start auto-polling if enabled
      if (this.autoReadWeight) {
        this.startWeightPolling();
      }
    } catch (error: any) {
      this.hardwareError = `Failed to connect to scale: ${error.message}`;
    }
  }
  
  async connectToPrinter(printerId: string) {
    if (!this.hardwareService) return;
    
    try {
      await this.hardwareService.connectToPrinter(printerId);
      this.connectedPrinter = this.availablePrinters.find(p => p.id === printerId) || null;
      this.hardwareError = null;
    } catch (error: any) {
      this.hardwareError = `Failed to connect to printer: ${error.message}`;
    }
  }
  
  async connectToScanner(scannerId: string) {
    if (!this.hardwareService) return;
    
    try {
      await this.hardwareService.connectToScanner(scannerId);
      this.connectedScanner = this.availableScanners.find(s => s.id === scannerId) || null;
      this.hardwareError = null;
    } catch (error: any) {
      this.hardwareError = `Failed to connect to scanner: ${error.message}`;
    }
  }
  
  async readScaleWeight() {
    if (!this.hardwareService || !this.connectedScale) return;

    // Don't start a new request if one is already in progress
    if (this.isReadingWeight) return;

    this.isReadingWeight = true;
    try {
      const weight = await this.hardwareService.getWeight(this.connectedScale.id);
      this.handleWeightUpdate({ weight });
      // Clear error on successful read
      this.hardwareError = null;
    } catch (error: any) {
      // Handle various expected errors gracefully
      if (error.message.includes('timeout') || error.message.includes('Read timeout')) {
        console.log('[ShippingLabelForm] Scale read timeout (expected) - waiting for weight updates via subscription');
        // Don't show error - timeout is expected, subscription will send updates when weight changes
      } else if (error.message.includes('Operation temporarily disabled') || error.message.includes('repeated failures')) {
        console.log('[ShippingLabelForm] Scale read operation disabled by UHB - relying on subscription for updates');
        // Don't show error - this happens when scale.read has failed too many times
        // The subscription mechanism still works fine for getting weight updates
      } else if (!error.message.includes('INSUFFICIENT_RESOURCES')) {
        this.hardwareError = `Failed to read weight: ${error.message}`;
      }
    } finally {
      this.isReadingWeight = false;
    }
  }
  
  handleWeightUpdate(reading: { weight: Weight }) {
    // Don't update if weight is locked
    if (this.isWeightLocked) return;
    
    // Clear any hardware errors on successful weight reading
    this.hardwareError = null;
    
    const previousValue = this.currentWeight?.value || 0;
    this.currentWeight = reading.weight;
    this.lastWeightUpdate = new Date();
    
    // Apply tare if set
    if (this.tareWeight && reading.weight.value > 0) {
      reading.weight.value = Math.max(0, reading.weight.value - this.tareWeight);
    }
    
    // Update weight trend
    if (previousValue && reading.weight.value) {
      const diff = reading.weight.value - previousValue;
      if (Math.abs(diff) < 0.1) {
        this.weightTrend = 'stable';
      } else if (diff > 0) {
        this.weightTrend = 'up';
      } else {
        this.weightTrend = 'down';
      }
    }
    
    // Add to history
    if (reading.weight.isStable && !reading.weight.isEmpty) {
      this.addToWeightHistory(reading.weight);
    }
    
    // Only update package weight if it's stable and not empty
    if (reading.weight.isStable && !reading.weight.isEmpty && this.autoReadWeight) {
      // Convert to ounces if needed
      let weightInOz = reading.weight.value;
      
      switch (reading.weight.unit) {
        case 'lbs':
          weightInOz = reading.weight.value * 16;
          break;
        case 'kg':
          weightInOz = reading.weight.value * 35.274;
          break;
        case 'g':
          weightInOz = reading.weight.value * 0.035274;
          break;
      }
      
      // Round to 1 decimal place
      const roundedWeight = Math.round(weightInOz * 10) / 10;

      if (this.isMultiPackageMode) {
        // In multi-package mode, update the active package
        if (this.packages[this.activePackageIndex]) {
          this.packages[this.activePackageIndex].weight = roundedWeight;
          console.log(`[ShippingLabelForm] Weight updated for active package ${this.activePackageIndex + 1}: ${roundedWeight}oz`);

          // Show snackbar feedback
          this.$nextTick(() => {
            (this as any).$toast?.success(`Package ${this.activePackageIndex + 1} weight updated: ${roundedWeight}oz`);
          });
        }
      } else {
        // In single package mode, update packageDetails (backward compatibility)
        this.packageDetails.weight = roundedWeight;
      }
    }
  }
  
  addToWeightHistory(weight: Weight) {
    // Clone the weight object
    const historicalWeight = { ...weight, timestamp: Date.now() };
    
    // Add to beginning of array
    this.weightHistory.unshift(historicalWeight);
    
    // Keep only the last N readings
    if (this.weightHistory.length > this.maxWeightHistory) {
      this.weightHistory.pop();
    }
  }
  
  startWeightPolling() {
    // With UHB, weight updates come via subscription (already active from connection)
    console.log('[ShippingLabelForm] Checking for cached weight from scale:', this.connectedScale?.id);

    if (this.connectedScale && this.autoReadWeight && this.hardwareService) {
      // Check if there's a cached weight from previous readings
      if ((this.hardwareService as any).getLastKnownWeight) {
        const cachedWeight = (this.hardwareService as any).getLastKnownWeight();
        if (cachedWeight && !cachedWeight.isEmpty) {
          console.log('[ShippingLabelForm] Found cached weight:', cachedWeight.value, cachedWeight.unit);
          this.handleWeightUpdate({ weight: cachedWeight });
        } else {
          console.log('[ShippingLabelForm] No cached weight - scale will update when weight changes or stabilizes');
        }
      }
    }
  }
  
  handlePrinterConnected(printer: Printer) {
    console.log('[ShippingLabelForm] Printer connected:', printer);
    this.connectedPrinter = printer;
    // Update selected printer ID if this is the printer we tried to connect to
    if (!this.selectedPrinterId || printer.id === this.selectedPrinterId) {
      this.selectedPrinterId = printer.id;
      console.log('[ShippingLabelForm] Selected printer is now connected');
    }
  }
  
  handleScannerConnected(scanner: Scanner) {
    console.log('[ShippingLabelForm] Scanner connected:', scanner);
    this.connectedScanner = scanner;
  }
  
  handlePackageScan(event: PackageScanEvent) {
    console.log('[ShippingLabelForm] Package scanned:', event);
    
    // Parse the QR code data
    if (event.qrData) {
      this.processScannedPackage(event.qrData);
    }
  }
  
  async processScannedPackage(qrData: string) {
    try {
      // Parse the QR code data
      const packageData = this.packageTypeService.parseQRCodeData(qrData);
      
      if (packageData && packageData.type === 'package') {
        // Get the full package type from the API
        const packageType = await this.packageTypeService.getPackageType(packageData.id);
        
        if (packageType) {
          this.applyPackageType(packageType);
          
          // Log usage
          await this.packageTypeService.logUsage(packageType.packageTypeId, 'scan', {
            orderId: this.orderId,
            deviceId: this.connectedScanner?.id
          });
          
          this.$emit('show-snackbar', {
            text: `Package type "${packageType.name}" applied`,
            color: 'success'
          });
        }
      }
    } catch (error) {
      console.error('Failed to process scanned package:', error);
      this.$emit('show-snackbar', {
        text: 'Invalid package QR code',
        color: 'error'
      });
    } finally {
      this.isScanning = false;
    }
  }
  
  async loadPackageTypes() {
    try {
      this.savedPackageTypes = await this.packageTypeService.getPackageTypes();
      // Filter to only active package types
      this.savedPackageTypes = this.savedPackageTypes.filter(pt => pt.isActive);
    } catch (error) {
      console.error('Failed to load package types:', error);
    }
  }
  
  async applyPackageType(packageType: PackageType) {
    this.packageDetails.length = packageType.length;
    this.packageDetails.width = packageType.width;
    this.packageDetails.height = packageType.height;

    // Convert weight from lbs to oz if provided
    if (packageType.weight) {
      this.packageDetails.weight = packageType.weight * 16; // Convert lbs to oz
    }

    // CRITICAL: Clear cached shipment ID when dimensions change
    // This ensures ShipStation receives the updated dimensions
    if (this.shipmentId) {
      console.log('[ShippingLabelForm] Clearing cached shipmentId due to dimension change via package type:', this.shipmentId);
      this.shipmentId = null;
    }


    this.appliedPackageType = packageType;
    
    // Log usage
    this.packageTypeService.logUsage(packageType.packageTypeId, 'manual', {
      orderId: this.orderId
    }).catch(error => {
      console.error('Failed to log package usage:', error);
    });
  }

  applyPackageTypeToActivePackage(packageType: PackageType) {
    const pkg = this.packages[this.activePackageIndex];
    if (!pkg) return;

    pkg.length = packageType.length;
    pkg.width = packageType.width;
    pkg.height = packageType.height;

    if (packageType.weight) {
      pkg.weight = packageType.weight * 16; // Convert lbs to oz
    }

    // Clear cached shipment ID when dimensions change
    if (this.shipmentId) {
      this.shipmentId = null;
    }

    // Log usage
    this.packageTypeService.logUsage(packageType.packageTypeId, 'manual', {
      orderId: this.orderId
    }).catch(error => {
      console.error('Failed to log package usage:', error);
    });
  }

  resetMenuScroll(refName: string, isOpen: boolean) {
    if (!isOpen) return;
    // Use requestAnimationFrame to ensure the menu content is rendered before resetting scroll
    requestAnimationFrame(() => {
      const menu = (this.$refs[refName] as any);
      const content = menu?.$refs?.content;
      if (content) {
        content.scrollTop = 0;
      }
    });
  }

  async handleGlobalPackageScan(event: any) {
    console.log('[ShippingLabelForm] *** GLOBAL PACKAGE QR SCANNED EVENT RECEIVED ***');
    console.log('[ShippingLabelForm] Event data:', event);
    console.log('[ShippingLabelForm] isModalVisible:', this.isModalVisible);
    console.log('[ShippingLabelForm] loadingRates:', this.loadingRates);
    console.log('[ShippingLabelForm] loadingBackgroundRates:', this.loadingBackgroundRates);

    // Only process if this modal is currently visible and not actively loading rates
    // Note: loadingBackgroundRates should not block package scans since new scans reset the process
    if (!this.isModalVisible || this.loadingRates) {
      console.log('[ShippingLabelForm] Modal not active or actively loading, ignoring global package scan');
      return;
    }

    try {
      // Check if this is a new scanner-friendly format with embedded dimensions
      if (event.packageData && event.packageData.dimensions &&
          event.packageData.dimensions.length > 0 &&
          event.packageData.dimensions.width > 0 &&
          event.packageData.dimensions.height > 0) {

        console.log('[ShippingLabelForm] *** NEW PKG FORMAT DETECTED - Using embedded dimensions ***');
        console.log('[ShippingLabelForm] Dimensions:', event.packageData.dimensions);

        // Enhanced diagnostic logging for QR scan processing
        console.log(`[QR-DEBUG] Starting QR scan processing`);
        console.log(`[QR-DEBUG] Current shipmentId: ${this.shipmentId}`);
        console.log(`[QR-DEBUG] Current externalShipmentId: ${this.pickTicketId ? `${this.orderData?.orderNumber}-PT${this.pickTicketId}` : `${this.orderData?.orderNumber}`}`);
        if (this.isMultiPackageMode) {
          // Multi-package mode: apply to active package
          const activePackage = this.packages[this.activePackageIndex];
          console.log(`[QR-DEBUG] Multi-package mode - applying to active package ${this.activePackageIndex + 1}`);
          console.log(`[QR-DEBUG] Before dimensions (Package ${this.activePackageIndex + 1}): L:${activePackage?.length} W:${activePackage?.width} H:${activePackage?.height}`);

          if (activePackage) {
            activePackage.length = event.packageData.dimensions.length;
            activePackage.width = event.packageData.dimensions.width;
            activePackage.height = event.packageData.dimensions.height;

            console.log(`[QR-DEBUG] After dimensions (Package ${this.activePackageIndex + 1}): L:${activePackage.length} W:${activePackage.width} H:${activePackage.height}`);

            // Auto-advance logic
            if (this.autoAdvancePackage) {
              if (this.activePackageIndex < this.packages.length - 1) {
                // Move to existing next package
                const nextPackageIndex = this.activePackageIndex + 1;
                console.log(`[QR-DEBUG] Auto-advancing to existing package ${nextPackageIndex + 1}`);
                this.selectPackage(nextPackageIndex);
              } else if (this.canAddMorePackages) {
                // On last package - auto-create a new empty package
                console.log(`[QR-DEBUG] On last package - auto-creating new empty package`);
                this.addPackage(true); // Pass true to initialize with empty/zero values
              }
            }
          }
        } else {
          // Single package mode: apply to packageDetails (backward compatibility)
          console.log(`[QR-DEBUG] Single package mode - applying to packageDetails`);
          console.log(`[QR-DEBUG] Before dimensions: L:${this.packageDetails.length} W:${this.packageDetails.width} H:${this.packageDetails.height}`);

          this.packageDetails.length = event.packageData.dimensions.length;
          this.packageDetails.width = event.packageData.dimensions.width;
          this.packageDetails.height = event.packageData.dimensions.height;

          console.log(`[QR-DEBUG] After dimensions: L:${this.packageDetails.length} W:${this.packageDetails.width} H:${this.packageDetails.height}`);
        }

        // Clear any existing rates since package details changed
        this.rates = [];
        this.selectedRateId = null;
        this.userOverrideRate = null;
        this.currentStep = 1; // Go back to package details step

        // CRITICAL: Clear cached shipment ID to force recreation with new dimensions
        // This ensures ShipStation receives the updated dimensions from QR scan
        if (this.shipmentId) {
          console.log(`[QR-DEBUG] Clearing shipmentId: ${this.shipmentId}`);
          console.log('[ShippingLabelForm] Clearing cached shipmentId due to dimension change via QR scan:', this.shipmentId);
          this.shipmentId = null;
        }


        // ENHANCED FIX: Clear any cached rates to force a fresh rate request
        // This ensures the backend gets called with new dimensions and creates a new shipment
        this.backgroundRates = [];

        console.log(`[QR-DEBUG] QR scan processing complete`);

        // Show success feedback
        const dimensions = `${event.packageData.dimensions.length}" × ${event.packageData.dimensions.width}" × ${event.packageData.dimensions.height}"`;
        let feedbackMessage = `Package dimensions applied from QR scan: ${dimensions}`;

        if (this.isMultiPackageMode) {
          feedbackMessage = `Package ${this.activePackageIndex + 1} dimensions applied from QR scan: ${dimensions}`;

          // Add auto-advance feedback if applicable
          if (this.autoAdvancePackage) {
            feedbackMessage += ` (Ready for Package ${this.activePackageIndex + 1})`;
          }
        }

        this.$emit('show-snackbar', {
          text: feedbackMessage,
          color: 'success'
        });

        console.log('[ShippingLabelForm] Successfully applied dimensions from PKG format QR code');
        return;
      }

      // Legacy path: Try to fetch package type from database
      const packageType = await this.packageTypeService.getPackageType(event.packageId);

      if (!packageType) {
        throw new Error(`Package type with ID ${event.packageId} not found`);
      }

      // Apply the package dimensions using existing logic
      this.applyPackageType(packageType);

      // Log the usage
      await this.packageTypeService.logUsage(packageType.packageTypeId, 'scan', {
        orderId: this.orderId,
        context: 'global-shipping-modal',
        deviceId: event.deviceId || 'unknown'
      });

      // Show success feedback
      this.$emit('show-snackbar', {
        text: `Applied dimensions from "${packageType.name}"`,
        color: 'success'
      });

      console.log('[ShippingLabelForm] Successfully applied package dimensions from legacy QR code');

    } catch (error: any) {
      console.error('[ShippingLabelForm] Failed to apply package from global scan:', error);

      // Show error feedback
      this.$emit('show-snackbar', {
        text: `Failed to apply package: ${error.message}`,
        color: 'error'
      });
    }
  }

  async startPackageScanning() {
    if (!this.hardwareService || !this.connectedScanner) return;
    
    this.isScanning = true;
    
    try {
      // The scanner will emit events when it scans
      // We're already listening for 'package-scan' events
      this.$emit('show-snackbar', {
        text: 'Ready to scan package QR code',
        color: 'info'
      });
    } catch (error) {
      console.error('Failed to start scanning:', error);
      this.isScanning = false;
      this.$emit('show-snackbar', {
        text: 'Failed to start scanner',
        color: 'error'
      });
    }
  }
  
  stopWeightPolling() {
    if (this.weightPollingInterval) {
      window.clearInterval(this.weightPollingInterval);
      this.weightPollingInterval = null;
    }
  }
  
  toggleAutoRead() {
    this.autoReadWeight = !this.autoReadWeight;
    if (this.autoReadWeight) {
      this.startWeightPolling();
    } else {
      this.stopWeightPolling();
    }
  }
  
  tareScale() {
    if (this.currentWeight && !this.currentWeight.isEmpty) {
      this.tareWeight = this.currentWeight.value;
      // Force a weight update
      this.readScaleWeight();
    }
  }
  
  resetTare() {
    this.tareWeight = 0;
    this.readScaleWeight();
  }
  
  lockWeight() {
    if (this.currentWeight && !this.currentWeight.isEmpty) {
      this.isWeightLocked = true;
      this.lockedWeight = { ...this.currentWeight };
      this.stopWeightPolling();
    }
  }
  
  unlockWeight() {
    this.isWeightLocked = false;
    this.lockedWeight = null;
    if (this.autoReadWeight) {
      this.startWeightPolling();
    }
  }
  
  handleHardwareConnected(status: any) {
    console.log('Hardware connected:', status);
  }
  
  handleHardwareDisconnected(error: any) {
    console.warn('Hardware disconnected:', error);
    this.hardwareAvailable = false;
  }
  
  handleScaleError(error: any) {
    this.hardwareError = `Scale error: ${error.error?.message || 'Unknown error'}`;
  }
  
  handleDevicesUpdated(data: { scales: Scale[]; printers: Printer[]; scanners?: Scanner[] }) {
    console.log('[ShippingLabelForm] Devices updated:', data);
    this.availableScales = data.scales;
    this.availablePrinters = data.printers;
    this.availableScanners = data.scanners || [];
    
    // Update connected devices if they exist in the new list
    if (this.connectedScale && !data.scales.find(s => s.id === this.connectedScale!.id)) {
      this.connectedScale = null;
    }
    if (this.connectedPrinter && !data.printers.find(p => p.id === this.connectedPrinter!.id)) {
      this.connectedPrinter = null;
    }
    if (this.connectedScanner && !this.availableScanners.find(s => s.id === this.connectedScanner!.id)) {
      this.connectedScanner = null;
    }
    
    // Auto-connect to first available scale if none connected
    if (!this.connectedScale && data.scales.length > 0) {
      const firstScale = data.scales[0];
      console.log('[ShippingLabelForm] Auto-connecting to first available scale from device update:', firstScale.manufacturer, firstScale.product);
      setTimeout(() => {
        this.connectToScale(firstScale.id);
      }, 100);
    }
    
    // Auto-connect to first available scanner if none connected
    if (!this.connectedScanner && this.availableScanners.length > 0) {
      this.connectToScanner(this.availableScanners[0].id);
    }
  }
  
  handleScaleConnected(scale: any) {
    console.log('[ShippingLabelForm] Scale connected:', scale);
    
    // First check if it's in availableScales
    let connectedScale = this.availableScales.find(s => s.id === scale.id);
    
    // If not found, use the scale object directly (it might be from auto-connection)
    if (!connectedScale && scale.id) {
      connectedScale = scale;
      // Add it to availableScales if not present
      if (!this.availableScales.find(s => s.id === scale.id)) {
        this.availableScales.push(scale);
      }
    }
    
    if (connectedScale) {
      this.connectedScale = connectedScale;
      this.hardwareError = null;
      console.log('[ShippingLabelForm] Scale connected successfully:', connectedScale);
      
      // Start auto-polling if enabled
      if (this.autoReadWeight) {
        this.startWeightPolling();
      }
    }
  }
  
  formatWeight(weight: Weight | null): string {
    if (!weight) return '--';
    
    const value = weight.value.toFixed(1);
    return `${value} ${weight.unit}`;
  }
  
  formatWeightLarge(weight: Weight | null): string {
    if (!weight || weight.isEmpty) return '0.0 lbs';
    
    // Apply smart rounding
    let value = weight.value;
    let decimals = 1;
    
    if (value < 1) {
      decimals = 2; // More precision for light items
    } else if (value > 10) {
      decimals = 0; // Less precision for heavy items
    }
    
    return `${value.toFixed(decimals)} ${weight.unit}`;
  }
  
  formatWeightNumber(weight: Weight | null): string {
    if (!weight || weight.isEmpty) return '0.0';
    
    // Apply smart rounding
    let value = weight.value;
    let decimals = 1;
    
    if (value < 1) {
      decimals = 2; // More precision for light items
    } else if (value > 10) {
      decimals = 0; // Less precision for heavy items
    }
    
    return value.toFixed(decimals);
  }
  
  formatTimeAgo(date: Date): string {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    
    if (seconds < 5) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    
    return date.toLocaleTimeString();
  }
  
  getWeightStatusColor(): string {
    if (!this.currentWeight) return 'grey';
    if (this.currentWeight.isEmpty) return 'grey';
    if (this.currentWeight.isStable) return 'green';
    return 'orange';
  }
  
  getWeightStatusText(): string {
    if (!this.currentWeight) return 'No reading';
    if (this.currentWeight.isEmpty && this.currentWeight.value === 0) {
      // For DYMO scales that only send data on weight changes
      return 'Ready';
    }
    if (this.currentWeight.isEmpty) return 'Empty';
    if (this.currentWeight.isStable) return 'Stable';
    return 'Unstable';
  }



  // Initialize label comments with pre-populated values
  async initializeLabelComments() {
    console.log('[ShippingLabelForm] Initializing label comments...');
    try {
      // Get current user initials from Vuex store (already calculated by store)
      const currentUser = this.$store.state.authentication.user;
      console.log('[ShippingLabelForm] Current user:', currentUser);
      if (currentUser && currentUser.initials) {
        this.currentUserInitials = currentUser.initials;
        console.log('[ShippingLabelForm] Current user initials:', this.currentUserInitials);
      }

      console.log('[ShippingLabelForm] Order data:', this.orderData);

      // Get sales rep ID from loaded order data
      // Note: salesRepId points to SalesRep table, which has a userId field
      // Prefer quote.salesRepId over order.salesRepId (quote is the source of truth)
      const salesRepId = this.orderData?.quote?.salesRepId || this.orderData?.salesRepId;
      console.log('[ShippingLabelForm] Sales rep ID to use:', salesRepId, '(from quote:', this.orderData?.quote?.salesRepId, ', from order:', this.orderData?.salesRepId, ')');

      if (salesRepId) {
        console.log('[ShippingLabelForm] Fetching sales rep info for sales rep ID:', salesRepId);
        // First fetch the SalesRep to get the userId
        try {
          const salesRepResponse = await this.$apollo.query({
            query: queries.LIST_SALES_REPS,
            variables: {
              offset: 0,
              take: 1,
              where: { salesRepId: { eq: salesRepId } }
            },
            fetchPolicy: 'cache-first'
          });

          console.log('[ShippingLabelForm] Sales rep response:', salesRepResponse.data);

          const salesRep = salesRepResponse.data?.salesReps?.items?.[0];
          if (salesRep?.userId) {
            console.log('[ShippingLabelForm] Found sales rep userId:', salesRep.userId);
            // Now fetch the user details using the userId
            const userResponse = await this.$apollo.query({
              query: GET_USER_EMAIL,
              variables: { userId: salesRep.userId },
              fetchPolicy: 'cache-first'
            });

            console.log('[ShippingLabelForm] User response:', userResponse.data);

            if (userResponse.data && userResponse.data.userById) {
              const user = userResponse.data.userById;
              // Use fullName if available, otherwise use username
              const userName = user.fullName || user.username || '';
              if (userName) {
                this.salesRepInitials = this.getUserInitials(userName);
                console.log('[ShippingLabelForm] Sales rep initials:', this.salesRepInitials);
              }
            }
          }
        } catch (error) {
          console.log('[ShippingLabelForm] Could not fetch sales rep info:', error);
        }
      }

      // For style code, we would need a separate query to get line items
      // For now, leave it empty and build the comment with what we have
      // TODO: Add style code when we have a query that includes line items

      // Build default comments
      this.defaultLabelComment1 = this.buildDefaultComment();
      this.labelComment1 = this.defaultLabelComment1;
      this.defaultLabelComment2 = this.orderData?.orderNumber || '';
      this.labelComment2 = this.defaultLabelComment2;
      console.log('[ShippingLabelForm] Default label comments:', this.defaultLabelComment1, this.defaultLabelComment2);

    } catch (error) {
      console.error('[ShippingLabelForm] Failed to initialize label comments:', error);
      // Continue without pre-population if there's an error
    }
  }

  // Label comment helper methods
  getUserInitials(name: string | null): string {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return parts.map(p => p[0]).join('').toUpperCase().substring(0, 3);
  }

  buildDefaultComment(): string {
    const initialsParts = [];

    // Add user initials (shipper)
    if (this.currentUserInitials) {
      initialsParts.push(this.currentUserInitials);
    }

    // Add sales rep initials if different from current user
    if (this.salesRepInitials && this.salesRepInitials !== this.currentUserInitials) {
      initialsParts.push(this.salesRepInitials);
    }

    // Build the comment: "TS/MC TL229728" format
    // Initials joined with /, style code separated by space
    let comment = initialsParts.join('/');

    if (this.firstLineItemStyleCode) {
      comment += (comment ? ' ' : '') + this.firstLineItemStyleCode;
    }

    return comment;
  }

  buildDefaultReturnComment(): string {
    const initialsParts = [];

    if (this.currentUserInitials) {
      initialsParts.push(this.currentUserInitials);
    }

    if (this.salesRepInitials && this.salesRepInitials !== this.currentUserInitials) {
      initialsParts.push(this.salesRepInitials);
    }

    // Return labels use order number instead of style code
    let comment = initialsParts.join('/');

    if (this.orderData?.orderNumber) {
      comment += (comment ? ' ' : '') + this.orderData.orderNumber;
    }

    return comment;
  }

  getDefaultPackageRma(packageSequence: number): string {
    const base = this.buildDefaultReturnComment();
    if (this.isMultiPackageMode && this.packages.length > 1) {
      const rma = `${base} P${packageSequence}`;
      return rma.length <= 25 ? rma : rma.substring(0, 25);
    }
    return base;
  }

  initPerPackageRma() {
    if (!this.isMultiPackageMode) return;
    const rma: { [key: number]: string } = {};
    this.packages.forEach((_, i) => {
      const seq = i + 1;
      rma[seq] = this.returnLabelRmaPerPackage[seq] || this.getDefaultPackageRma(seq);
    });
    this.returnLabelRmaPerPackage = rma;
  }

  onPerPackageRmaToggle(val: boolean) {
    if (val) {
      this.initPerPackageRma();
    }
  }

  onPerPackageLabelCommentsToggle(val: boolean) {
    if (val) {
      this.initPerPackageLabelComments();
    }
  }

  initPerPackageLabelComments() {
    if (!this.isMultiPackageMode) return;
    const comments: { [key: number]: { reference1: string; reference2: string; reference3: string } } = {};
    this.packages.forEach((_, i) => {
      const seq = i + 1;
      comments[seq] = this.labelCommentsPerPackage[seq] || {
        reference1: this.labelComment1 || '',
        reference2: this.buildPerPackageRef2(seq),
        reference3: this.labelComment3 || ''
      };
    });
    this.labelCommentsPerPackage = comments;
  }

  buildPerPackageRef2(packageSequence: number): string {
    const base = this.labelComment2 || this.defaultLabelComment2 || '';
    const suffix = ` P${packageSequence}`;
    const maxLen = this.getMaxCharacters(2);
    if (!base) return `P${packageSequence}`;
    const combined = base + suffix;
    return maxLen > 0 && combined.length > maxLen ? combined.substring(0, maxLen) : combined;
  }

  resetPerPackageLabelComments(packageSequence: number) {
    this.$set(this.labelCommentsPerPackage, packageSequence, {
      reference1: this.defaultLabelComment1 || '',
      reference2: this.buildPerPackageRef2(packageSequence),
      reference3: ''
    });
  }

  getMaxCharacters(lineNumber: number): number {
    const carrierCode = this.selectedRate?.carrierCode?.toLowerCase();

    const limits: { [key: string]: number[] } = {
      'usps': [60, 60, 60],
      'fedex': [35, 30, 30],
      'fedex_walleted': [35, 30, 30],
      'ups': [35, 35, 0],
      'ontrac': [25, 25, 0],
      'dhl': [35, 0, 0]
    };

    const carrierLimits = limits[carrierCode || ''] || [35, 35, 35];
    return carrierLimits[lineNumber - 1] || 0;
  }

  getCarrierSupportMessage(): string {
    const carrierCode = this.selectedRate?.carrierCode?.toLowerCase();

    const messages: { [key: string]: string } = {
      'usps': 'USPS supports all 3 reference lines (60 chars each)',
      'fedex': 'FedEx supports 3 lines (35/30/30 chars)',
      'fedex_walleted': 'FedEx supports 3 lines (35/30/30 chars)',
      'ups': 'UPS supports 2 reference lines (35 chars each)',
      'ontrac': 'OnTrac supports 2 lines (25 chars each)',
      'dhl': 'DHL Express combines all references into one field'
    };

    return messages[carrierCode || ''] || 'Carrier label message support varies';
  }

  resetToDefaultComment(lineNumber: number) {
    if (lineNumber === 1) {
      this.labelComment1 = this.defaultLabelComment1;
    }
  }

  getReturnLabelMessagesInput() {
    if (!this.createReturnLabel) return null;
    // In multi-package mode with per-package toggle ON, use per-package input instead
    if (this.isMultiPackageMode && this.perPackageRma) return null;
    if (!this.returnLabelRma) return null;
    return {
      reference1: this.returnLabelRma || null,
      reference2: null,
      reference3: null
    };
  }

  getReturnLabelRmaPerPackageInput(): any[] | null {
    if (!this.createReturnLabel || !this.isMultiPackageMode || !this.perPackageRma) return null;
    const result: any[] = [];
    for (const seq of this.returnLabelPackageSelections) {
      const rma = this.returnLabelRmaPerPackage[seq];
      if (rma) {
        result.push({ packageSequence: seq, rmaNumber: rma });
      }
    }
    return result.length > 0 ? result : null;
  }

  buildMultiPackageInput(validPackages: any[]) {
    return {
      packages: validPackages.map((pkg: any, index: number) => {
        const pkgInput: any = {
          weight: pkg.weight,
          length: pkg.length,
          width: pkg.width,
          height: pkg.height,
          externalPackageId: pkg.id
        };
        // Include per-package label messages when toggle is on
        if (this.perPackageLabelComments) {
          const seq = index + 1;
          const comments = this.labelCommentsPerPackage[seq];
          if (comments) {
            pkgInput.labelMessages = {
              reference1: comments.reference1 || null,
              reference2: comments.reference2 || null,
              reference3: comments.reference3 || null
            };
          }
        }
        return pkgInput;
      }),
      validateCarrierSupport: true
    };
  }

  getLabelMessagesInput() {
    // DIAGNOSTIC: Log what we're sending
    console.log('[ShippingLabelForm] getLabelMessagesInput called:', {
      labelComment1: this.labelComment1,
      labelComment2: this.labelComment2,
      labelComment3: this.labelComment3
    });

    // Only return label messages if at least one is not empty
    if (!this.labelComment1 && !this.labelComment2 && !this.labelComment3) {
      console.log('[ShippingLabelForm] All label comments empty, returning null');
      return null;
    }

    const result = {
      reference1: this.labelComment1 || null,
      reference2: this.labelComment2 || null,
      reference3: this.labelComment3 || null
    };

    console.log('[ShippingLabelForm] Returning label messages:', result);
    return result;
  }

  /**
   * Check if the current label messages or emails have changed from the snapshot
   * taken when the shipment was last created. Returns true if either has changed.
   */
  hasShipmentContextChanged(): boolean {
    // If we haven't fetched rates yet, nothing has changed
    if (!this.lastFetchedLabelMessages && !this.lastFetchedEmails) {
      return false;
    }

    // Check label messages
    const currentMessages = {
      reference1: this.labelComment1 || null,
      reference2: this.labelComment2 || null,
      reference3: this.labelComment3 || null
    };

    const messagesChanged = this.lastFetchedLabelMessages &&
      (currentMessages.reference1 !== this.lastFetchedLabelMessages.reference1 ||
       currentMessages.reference2 !== this.lastFetchedLabelMessages.reference2 ||
       currentMessages.reference3 !== this.lastFetchedLabelMessages.reference3);

    // Check emails
    const currentEmails = this.getEmailsAsString() || null;
    const emailsChanged = this.lastFetchedEmails !== currentEmails;

    if (messagesChanged || emailsChanged) {
      console.log('[ShippingLabelForm] Shipment context changed detected:', {
        messagesChanged,
        emailsChanged,
        previous: {
          messages: this.lastFetchedLabelMessages,
          emails: this.lastFetchedEmails
        },
        current: {
          messages: currentMessages,
          emails: currentEmails
        }
      });
    }

    return messagesChanged || emailsChanged;
  }

  beforeDestroy() {
    // Stop polling
    this.stopWeightPolling();

    // Remove event listeners
    this.$bus?.$off('printer-settings-updated', this.onPrinterSettingsUpdated);

    // Clean up global scanner event service
    if (this.scannerEventService) {
      this.scannerEventService.removeListener('package-qr-scanned', this.handleGlobalPackageScan);
    }

    // Clean up hardware service
    if (this.hardwareService) {
      this.hardwareService.off('weight', this.handleWeightUpdate);
      this.hardwareService.off('connected', this.handleHardwareConnected);
      this.hardwareService.off('disconnected', this.handleHardwareDisconnected);
      this.hardwareService.off('scale-error', this.handleScaleError);
      this.hardwareService.off('devices-updated', this.handleDevicesUpdated);
      this.hardwareService.off('scale-connected', this.handleScaleConnected);
      this.hardwareService.off('printer-connected', this.handlePrinterConnected);
      this.hardwareService.off('scanner-connected', this.handleScannerConnected);
      this.hardwareService.off('package-scan', this.handlePackageScan);
    }
  }
  
  
  // Transform snake_case rate properties to camelCase
  transformRateToCamelCase(rate: any): Rate {
    console.log('DEBUG transformRateToCamelCase input:', rate);
    
    const transformed = {
      rateId: rate.rate_id || rate.rateId || `generated_${Math.random().toString(36).substr(2, 9)}`,
      rateType: rate.rate_type || rate.rateType,
      carrierId: rate.carrier_id || rate.carrierId,
      carrierCode: rate.carrier_code || rate.carrierCode,
      carrierFriendlyName: rate.carrier_friendly_name || rate.carrierFriendlyName,
      carrierNickname: rate.carrier_nickname || rate.carrierNickname,
      serviceCode: rate.service_code || rate.serviceCode,
      serviceType: rate.service_type || rate.serviceType,
      packageType: rate.package_type || rate.packageType,
      shippingAmount: rate.shipping_amount || rate.shippingAmount,
      insuranceAmount: rate.insurance_amount || rate.insuranceAmount,
      confirmationAmount: rate.confirmation_amount || rate.confirmationAmount,
      otherAmount: rate.other_amount || rate.otherAmount,
      taxAmount: rate.tax_amount || rate.taxAmount,
      zone: rate.zone,
      deliveryDays: rate.delivery_days || rate.deliveryDays,
      guaranteedService: rate.guaranteed_service || rate.guaranteedService,
      estimatedDeliveryDate: rate.estimated_delivery_date || rate.estimatedDeliveryDate,
      carrierDeliveryDays: rate.carrier_delivery_days || rate.carrierDeliveryDays,
      shipDate: rate.ship_date || rate.shipDate,
      negotiatedRate: rate.negotiated_rate || rate.negotiatedRate,
      trackable: rate.trackable,
      validationStatus: rate.validation_status || rate.validationStatus,
      warningMessages: rate.warning_messages || rate.warningMessages || [],
      errorMessages: rate.error_messages || rate.errorMessages || []
    };
    
    console.log('DEBUG transformRateToCamelCase output:', transformed);
    return transformed;
  }

}
